import { getTimelineFileError, timelineUploadCancelled } from './timelineFileUpload';
import { Constants } from './Constants';
import { 
    collection, 
    deleteField,
    getDocs,
    getDocsFromServer,
    getDoc,
    getDocFromServer,
    query,
    where,
    doc,
    updateDoc,
    addDoc,
    setDoc,
    deleteDoc,
    serverTimestamp,
    onSnapshot,
    runTransaction,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage';

import { 
    AssessmentDefinition,
    App, 
    ClientActivityPoint,
    ClientActivityState,
    Client, 
    ClientBriefing,
    ClientBriefingState,
    ClientContentState,
    ClientPlanCatalogue,
    ClientPlanOverride,
    GeneratedClientPlan,
    ClientsState, 
    CommsCampaign,
    CommsCampaignsState,
    CommsCampaignStage,
    Condition, 
    ConditionsState, 
    CreateDashboardUserRequest,
    Data, 
    DashboardCustomRole,
    DashboardFeatureAccess,
    DashboardFeatureAccessByRole,
    DashboardFeature,
    DashboardRole,
    DashboardUserVerificationResult,
    Family,
    HealthcareProvider, 
    HealthcareProviderInfo, 
    HealthcareProviderMessagingReplyTo,
    HealthcareProviderProfile,
    HealthcareProviderProfileUpdate,
    HealthcareProvidersState, 
    LicenseUsageSummary,
    ProviderEntitlementSnapshot,
    Query, 
    Queryable, 
    ReportAttachmentContent,
    ReportTemplate,
    SpeechNeed, 
    SpeechNeedsState,
    TherapySession,
    TherapySessionPlacedTag,
    TherapySessionReport,
    TherapySessionReportGenerationRequest,
    TherapySessionReportsState,
    TherapySessionTagDefinition,
    TherapySessionTranscriptUtterance,
    TherapySessionsState,
    TimelineFileUploadOptions,
    TimelineDocument,
    TimelineDocumentsState,
    User,
    UserResponseRecord,
    UserResponseKeysState,
    Therapist,
    TherapistsState,
} from './interfaces';

import { newQueryable } from './Queryable';

import { auth, db, storage } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { getClientSpeechNeeds, isCareRecipientClient, withClientSpeechNeeds } from './clientSpeechNeeds';
import { speechNeedsVisibleToProvider } from './providerSpeechNeeds';
import {
    canAssignDashboardRole,
    canConfigureProviderDashboardAccess,
    canViewProviderTeam,
    CONFIGURABLE_DASHBOARD_FEATURES,
    DASHBOARD_FEATURES,
    getDashboardRole,
    hasDashboardFeature,
    isRootOrSuperRole,
} from './DashboardAccess';
import { agreementProviderIdFromLocation } from './saas/DashboardMigrationReturn';
import { initializeDashboardAccess } from './DashboardAuth';
import { fetchProviderClientActivityDocuments } from './ProviderActivityApi';
import { getClientActivityIdentifiers, getMobileAppUsageByClientId } from './familyAppUsage';
import { parseProviderBillingFields } from './saas/providerBilling';
import { getFirstFirestoreTimestampDate, reviveCallableFirestoreTimestamps } from './firestoreTimestamp';
import { createDefaultCommsCampaignMessages } from './commsCampaignDefaults';
import { formatClientContentError, parseClientContent } from './clientContent';
import {
    buildClientPlanCatalogue, CLIENT_PLAN_MAX_TOPICS, ClientPlanCatalogueScope,
    clientPlanConditionSpeechNeedIds, isClientPlanGoalAvailableToProvider, parseClientPlanTopic,
} from './clientContentCatalogue';

const TABLE_NAME_PAGES = 'Pages';
const TABLE_NAME_GOALS = 'Goals';
const TABLE_NAME_SECTIONS = 'Sections';
const TABLE_NAME_CLIENTS = 'Clients';
const TABLE_NAME_USER_RESPONSES = 'UserResponses';
const TABLE_NAME_FAMILIES = 'Families';
const TABLE_NAME_CONDITIONS = 'Conditions';
const TABLE_NAME_SPEECH_NEEDS = 'SpeechNeeds';
const TABLE_NAME_GOAL_CATEGORIES = 'GoalCategories';
const TABLE_NAME_USER_FEEDBACK = 'UserFeedback';
const TABLE_NAME_ASSESSMENT_SCHEDULES = 'AssessmentSchedules';
const TABLE_NAME_ASSESSMENTS = 'Assessments';
const TABLE_NAME_HEALTHCARE_PROVIDERS = 'HealthcareProviders';
const TABLE_NAME_COMMS_CAMPAIGNS = 'CommsCampaigns';
const TABLE_NAME_SESSIONS = 'Sessions';
const TABLE_NAME_REPORTS = 'Reports';
const TABLE_NAME_TIMELINE_DOCUMENTS = 'TimelineDocuments';
const CLIENTS_REFRESH_INTERVAL_MS = 30 * 1000;
const THERAPY_SESSION_REPORT_CALLABLE_TIMEOUT_MS = 10 * 60 * 1000;
const THERAPY_SESSION_REPORT_POLL_INTERVAL_MS = 5 * 1000;
const THERAPY_SESSION_REPORT_MAX_POLL_ATTEMPTS = 144;

type ScopedAccessSnapshot = {
    generation: number;
    healthcareProviderId: string;
};

// Only explicit access failures invalidate cached data; a failed refresh is not an empty result.
function isDataAccessDeniedError(error: unknown): boolean {
    const details = error && typeof error === 'object' ? error as {code?: unknown} : {};
    const code = typeof details.code === 'string' ? details.code.replace(/^(?:firestore|functions)\//, '') : '';
    return code === 'permission-denied' || code === 'unauthenticated';
}

// Plan assignments retain the author as well as the selected provider throughout each request.
type ClientPlanAccess = { scope: ScopedAccessSnapshot; userId: string };

type SessionPlacedTagEdit = {
    addedTags: Array<TherapySessionPlacedTag>;
    removedIds: Set<string>;
    resolve: () => void;
    reject: (error: unknown) => void;
};

type SessionPlacedTagEditState = {
    access: ScopedAccessSnapshot;
    scopeKey: string;
    userId: string | undefined;
    confirmedTags: Array<TherapySessionPlacedTag>;
    pending: Array<SessionPlacedTagEdit>;
    revision: number;
    saving: boolean;
};

// Replay independent marker additions/removals without losing another rapid click.
function applySessionPlacedTagEdits(
    confirmedTags: Array<TherapySessionPlacedTag>,
    edits: Array<SessionPlacedTagEdit>
): Array<TherapySessionPlacedTag> {
    const tagsById = new Map(confirmedTags.map((tag) => [tag.id, tag]));
    edits.forEach((edit) => {
        edit.removedIds.forEach((id) => tagsById.delete(id));
        edit.addedTags.forEach((tag) => tagsById.set(tag.id, tag));
    });
    return Array.from(tagsById.values()).sort((left, right) => left.timestampMs - right.timestampMs);
}

function speechNeedSetsMatch(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
        return false;
    }

    const aSet = new Set(a);
    return b.every((speechNeedId) => aSet.has(speechNeedId));
}

// Collect both legacy and current speech-need fields for export rows.
function getAllClientSpeechNeedIds(client: Pick<Client, 'speechNeed' | 'speechNeeds'>): string[] {
    return Array.from(new Set([
        ...getClientSpeechNeeds({ speechNeed: client.speechNeed }),
        ...getClientSpeechNeeds({ speechNeeds: client.speechNeeds }),
    ]));
}

// Add speech needs to a per-user export index without duplicating ids.
function addSpeechNeedsForExportUser(
    speechNeedsByUserId: Map<string, Set<string>>,
    userId: string | undefined,
    speechNeedIds: Array<string>
) {
    if (!userId || speechNeedIds.length === 0) {
        return;
    }

    const existingSpeechNeedIds = speechNeedsByUserId.get(userId) || new Set<string>();
    speechNeedIds.forEach((speechNeedId) => existingSpeechNeedIds.add(speechNeedId));
    speechNeedsByUserId.set(userId, existingSpeechNeedIds);
}

function normalizeCareRecipientMatchValue(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function getCareRecipientIdentityKey(client: Partial<Client>): string | null {
    const givenName = normalizeCareRecipientMatchValue(client.givenName);
    const familyName = normalizeCareRecipientMatchValue(client.familyName);
    const dateOfBirth = normalizeCareRecipientMatchValue(client.dateOfBirth);

    if (!givenName || !familyName || !dateOfBirth) {
        return null;
    }

    return `${givenName}::${familyName}::${dateOfBirth}`;
}

function getCareRecipientCarerIds(client: Partial<Client>): string[] {
    return Array.from(new Set(
        (Array.isArray(client.carers) ? client.carers : [])
            .filter((carerId): carerId is string => typeof carerId === 'string' && carerId.trim() !== '')
    ));
}

function careRecipientsMatchIdentity(a: Partial<Client>, b: Partial<Client>): boolean {
    const aKey = getCareRecipientIdentityKey(a);
    return Boolean(aKey && aKey === getCareRecipientIdentityKey(b));
}

function getTimestampMillis(value: any): number {
    if (!value) {
        return Number.MAX_SAFE_INTEGER;
    }

    if (typeof value.toMillis === 'function') {
        return value.toMillis();
    }

    if (value instanceof Date) {
        return value.getTime();
    }

    if (typeof value.seconds === 'number') {
        return value.seconds * 1000;
    }

    return Number.MAX_SAFE_INTEGER;
}

// Generate stable ids for campaign messages when they are first saved.
function createCampaignMessageId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `msg-${crypto.randomUUID()}`;
    }

    return `msg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Collapse removed trigger states onto the supported equivalents.
function normalizeCampaignMessageSince(
    since: string
): CommsCampaign['messages'][number]['since'] {
    return since as CommsCampaign['messages'][number]['since'];
}

// Normalize old single-stage messages into the current multi-stage model.
function normalizeCampaignMessageStages(
    message: CommsCampaign['messages'][number]
): Array<CommsCampaign['messages'][number]['stages'][number]> {
    if (Array.isArray(message.stages) && message.stages.length > 0) {
        return message.stages;
    }

    return message.stage ? [message.stage] : ['referral_created_not_signed_in'];
}

// Ensure every campaign message has a stable id and supported message shape
// before it is displayed or persisted.
function ensureCampaignMessagesHaveIds(messages: Array<CommsCampaign['messages'][number]>) {
    let changed = false;
    const nextMessages = messages.map((message) => {
        const rawSince = (message as { since: string }).since;
        const normalizedSince = normalizeCampaignMessageSince(rawSince);
        const normalizedStages = normalizeCampaignMessageStages(message);

        if (
            message.id &&
            normalizedSince === rawSince &&
            Array.isArray(message.stages) &&
            message.stages.length > 0 &&
            !message.stage
        ) {
            return message;
        }

        changed = true;
        const { stage: _legacyStage, ...messageWithoutLegacyStage } = message;
        return {
            ...messageWithoutLegacyStage,
            since: normalizedSince,
            stages: normalizedStages,
            id: message.id || createCampaignMessageId(),
        };
    });

    return {
        messages: nextMessages,
        changed,
    };
}

export class DataImpl implements Data {

    private app: App;

    // healthcare providers
    private healthcareProviderId: string = '';
    private healthcareProviders: Array<HealthcareProvider> = [];
    private healthcareProvidersQueryable!: Queryable<HealthcareProvidersState>;
    private healthcareProviderInfo: HealthcareProviderInfo = {
        id: '',
        name: '',
        inviteCodes: [],
        dashboardUrl: '',
        licenses: undefined,
        postcodesAccess: [],
        reportTemplates: [],
        dashboardCustomRoles: [],
    };
    private healthcareProviderInfoQueryable!: Queryable<HealthcareProviderInfo>;
    private commsCampaigns: Array<CommsCampaign> = [];
    private commsCampaignsQueryable!: Queryable<CommsCampaignsState>;

    // conditions
    private conditions: Array<Condition> = [];
    private selectedCondition: string = '';
    private conditionsQueryable!: Queryable<ConditionsState>;

    // therapists
    private therapists: Array<Therapist> = [];
    private therapistsIncludeTeamRoleFields: boolean = false;
    private therapistsFetchRequestId: number = 0;
    private therapistForCurrentUser: Therapist = {
        id: '',
        name: '',
        healthcareProviderId: '',
        super: false,
        role: 'healthcare_professional',
        email: '',
    };
    private currentTherapistQueryable!: Queryable<Therapist>;
    private therapistsInCurrentHealthcareProviderQueryable!: Queryable<TherapistsState>;

    // speech needs
    private speechNeeds: Array<SpeechNeed> = [];
    private speechNeedsQueryable!: Queryable<SpeechNeedsState>;
    private allSpeechNeedsQueryable!: Queryable<SpeechNeedsState>;

    // clients
    private allClients: Array<Client> = [];
    private clients: Array<Client> = [];
    private clientsQueryable!: Queryable<ClientsState>;
    private therapySessions: Array<TherapySession> = [];
    private therapySessionsQueryable!: Queryable<TherapySessionsState>;
    private therapySessionsFetchRequestId = 0;
    private therapySessionsFetchesInFlight = new Map<string, number>();
    private sessionPlacedTagEdits = new Map<string, SessionPlacedTagEditState>();
    private therapySessionReports: Array<TherapySessionReport> = [];
    private therapySessionReportsQueryable!: Queryable<TherapySessionReportsState>;
    private timelineDocuments: Array<TimelineDocument> = [];
    private timelineDocumentsQueryable!: Queryable<TimelineDocumentsState>;
    private therapySessionReportPollingIds: Set<string> = new Set();
    private therapySessionReportPollingAttemptsById: Record<string, number> = {};
    private therapySessionReportPollingTimerId: ReturnType<typeof setTimeout> | null = null;
    private clientActivityByClientId: Record<string, Array<number>> = {};
    private clientActivityTimelineByClientId: Record<string, Array<ClientActivityPoint>> = {};
    private clientSessionMinutesTimelineByClientId: Record<string, Array<ClientActivityPoint>> = {};
    private clientSignUpTimeByClientId: Record<string, string> = {};
    private clientStageByClientId: Record<string, CommsCampaignStage> = {};
    private clientMobileAppUsageByClientId: Record<string, boolean> = {};
    private clientMobileAppUsageStatus: NonNullable<ClientActivityState['mobileAppUsageStatus']> = 'loading';
    private clientActivityQueryable!: Queryable<ClientActivityState>;
    private userResponseKeys: Array<string> = [];
    private userResponseKeysQueryable!: Queryable<UserResponseKeysState>;

    // current user
    private user?: User;
    private clientsRefreshIntervalId: ReturnType<typeof setInterval> | null = null;
    private clientsFetchInFlightScopes: Set<string> = new Set();
    private scopedDataGeneration: number = 0;
    private healthcareProvidersFetchGeneration: number = 0;
    private healthcareProviderSubscriptionGeneration: number = 0;
    private selfServiceEntitlementFetchGeneration: number = 0;
    private therapistLookupGeneration: number = 0;
    private currentTherapistUnsubscribe: (() => void) | null = null;
    private currentHealthcareProviderUnsubscribe: (() => void) | null = null;
    private clientContentSubscription: {
        clientId: string;
        providerId: string;
        userId: string;
        version: number;
        onChange: (state: ClientContentState) => void;
        unsubscribe?: () => void;
    } | null = null;
    private clientBriefingSubscription: {
        clientId: string;
        providerId: string;
        userId: string;
        version: number;
        onChange: (state: ClientBriefingState) => void;
        unsubscribe?: () => void;
    } | null = null;

    // Listen to the selected app user's published content without fetching other family members.
    subscribeClientContent(clientId: string, onChange: (state: ClientContentState) => void): () => void {
        this.clearClientContentSubscription();
        const subscription = {
            clientId,
            providerId: this.healthcareProviderId,
            userId: this.user?.authenticatedUser?.uid || '',
            version: 0,
            onChange,
        };
        this.clientContentSubscription = subscription;
        void this.startClientContentSubscription();
        return () => {
            if (this.clientContentSubscription === subscription) {
                this.clearClientContentSubscription();
            }
        };
    }

    // Load the provider's available topic hierarchy and the latest assignment for this app user.
    async loadClientPlanCatalogue(clientId: string): Promise<ClientPlanCatalogue> {
        const access = this.requireClientPlanAccess(clientId);
        const [clientSnapshot, providerSnapshot] = await Promise.all([
            getDocFromServer(doc(db, TABLE_NAME_CLIENTS, clientId)),
            getDocFromServer(doc(db, TABLE_NAME_HEALTHCARE_PROVIDERS, access.scope.healthcareProviderId)),
        ]);
        this.assertClientPlanAccess(access);
        this.requirePlanAppClient(clientSnapshot.exists() ? clientSnapshot.data() : null, access);
        const catalogueScope = this.clientPlanCatalogueScope(providerSnapshot.exists() ? providerSnapshot.data() : null, access);

        // Catalogue reads are public, but the editor and private assignment remain Clients-scoped.
        const [speechNeeds, conditions, categories, goals, overrideSnapshot] = await Promise.all([
            getDocs(collection(db, TABLE_NAME_SPEECH_NEEDS)),
            getDocs(collection(db, TABLE_NAME_CONDITIONS)),
            getDocs(collection(db, TABLE_NAME_GOAL_CATEGORIES)),
            getDocs(collection(db, TABLE_NAME_GOALS)),
            getDocFromServer(doc(db, TABLE_NAME_CLIENTS, clientId, 'Content', 'override')),
        ]);
        this.assertClientPlanAccess(access);
        const catalogue = buildClientPlanCatalogue({
            speechNeeds: speechNeeds.docs.map((snapshot) => ({ ...snapshot.data(), id: snapshot.id })),
            conditions: conditions.docs.map((snapshot) => ({ ...snapshot.data(), id: snapshot.id })),
            categories: categories.docs.map((snapshot) => ({ ...snapshot.data(), id: snapshot.id })),
            goals: goals.docs.map((snapshot) => ({ ...snapshot.data(), id: snapshot.id })),
        }, 'en', catalogueScope);
        const override = overrideSnapshot.exists() ? overrideSnapshot.data() : null;
        let activeOverride: ClientPlanOverride | null = null;
        if (override) {
            if (override.schemaVersion !== 1 || override.clientId !== clientId ||
                override.providerId !== access.scope.healthcareProviderId ||
                !['active', 'completed'].includes(override.status)) {
                throw new Error('The assigned plan could not be read. Ask your service administrator to check this client’s plan.');
            }
            if (override.status === 'active') {
                this.validateClientPlanGoalIds(override.goalIds);
                if (typeof override.revision !== 'string' || !override.revision || override.revision.length > 200) {
                    throw new Error('The assigned plan has an invalid revision. Ask your service administrator to check this client’s plan.');
                }
                activeOverride = { revision: override.revision, goalIds: [...override.goalIds] };
            }
        }
        return { ...catalogue, activeOverride };
    }

    // Keep patient context and credentials on the server; late results cannot cross account/provider switches.
    async generateClientPlan(clientId: string, instructions: string): Promise<GeneratedClientPlan> {
        const access = this.requireClientPlanAccess(clientId);
        if (typeof instructions !== 'string' || instructions.length > 4000) {
            throw new Error('Keep plan instructions within 4,000 characters.');
        }
        const request = httpsCallable<{ clientId: string; instructions: string }, GeneratedClientPlan>(
            getFunctions(), 'generateClientPlan', { timeout: 180000 }
        );
        const result = await request({ clientId, instructions: instructions.trim() });
        this.assertClientPlanAccess(access);
        return result.data;
    }

    // Atomically validate live membership and topic availability before replacing the assignment.
    async submitClientPlanOverride(clientId: string, goalIds: string[]): Promise<ClientPlanOverride> {
        this.validateClientPlanGoalIds(goalIds);
        const access = this.requireClientPlanAccess(clientId);
        const assignment = {
            revision: doc(collection(db, TABLE_NAME_CLIENTS, clientId, 'Content')).id,
            goalIds: [...goalIds],
        };
        await runTransaction(db, async (transaction) => {
            const [clientSnapshot, providerSnapshot] = await Promise.all([
                transaction.get(doc(db, TABLE_NAME_CLIENTS, clientId)),
                transaction.get(doc(db, TABLE_NAME_HEALTHCARE_PROVIDERS, access.scope.healthcareProviderId)),
            ]);
            this.assertClientPlanAccess(access);
            this.requirePlanAppClient(clientSnapshot.exists() ? clientSnapshot.data() : null, access);
            const catalogueScope = this.clientPlanCatalogueScope(providerSnapshot.exists() ? providerSnapshot.data() : null, access);
            const goals = await Promise.all(assignment.goalIds.map((goalId) =>
                transaction.get(doc(db, TABLE_NAME_GOALS, goalId))
            ));
            this.assertClientPlanAccess(access);
            if (goals.some((snapshot) => !snapshot.exists() ||
                !parseClientPlanTopic({ ...snapshot.data(), id: snapshot.id }))) {
                throw new Error('A selected topic is no longer available. Reload the catalogue and update your plan.');
            }

            // Recheck the live provider/condition path, including access revoked since the editor opened.
            const goalRecords: Array<Record<string, unknown>> = goals.map((snapshot) => ({ ...snapshot.data(), id: snapshot.id }));
            const conditionIds = Array.from(new Set(goalRecords.flatMap((goal) => this.clientPlanDocumentIds(goal.targetConditions))));
            const conditionSnapshots = await Promise.all(conditionIds.map((id) => transaction.get(doc(db, TABLE_NAME_CONDITIONS, id))));
            this.assertClientPlanAccess(access);
            const conditions = new Map<string, Record<string, unknown>>(conditionSnapshots
                .filter((snapshot) => snapshot.exists())
                .map((snapshot) => [snapshot.id, { ...snapshot.data(), id: snapshot.id }]));
            const needIds = this.clientPlanDocumentIds(Array.from(conditions.values()).flatMap((condition) =>
                clientPlanConditionSpeechNeedIds(condition, catalogueScope)));
            const needSnapshots = await Promise.all(needIds.map((id) => transaction.get(doc(db, TABLE_NAME_SPEECH_NEEDS, id))));
            this.assertClientPlanAccess(access);
            const knownNeedIds = new Set(needSnapshots.filter((snapshot) => snapshot.exists()).map((snapshot) => snapshot.id));
            if (goalRecords.some((goal) => !isClientPlanGoalAvailableToProvider(goal, conditions, knownNeedIds, catalogueScope))) {
                throw new Error('A selected topic is no longer available for this healthcare provider. Reload the catalogue and update your plan.');
            }

            // Replace the full document so an earlier completion receipt cannot retire the new plan.
            transaction.set(doc(db, TABLE_NAME_CLIENTS, clientId, 'Content', 'override'), {
                schemaVersion: 1,
                clientId,
                providerId: access.scope.healthcareProviderId,
                ...assignment,
                status: 'active',
                createdBy: access.userId,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                completedAt: null,
                completedRevision: null,
            });
        });
        this.assertClientPlanAccess(access);
        return assignment;
    }

    // An absent allowlist is unrestricted by the provider model; an explicit empty list allows none.
    private clientPlanCatalogueScope(provider: Record<string, unknown> | null, access: ClientPlanAccess): ClientPlanCatalogueScope {
        if (!provider) {
            throw new Error('This healthcare provider is no longer available. Refresh the page and try again.');
        }
        // The fresh provider record also protects edits racing a platform availability change.
        if (!hasDashboardFeature(this.parseHealthcareProvider(access.scope.healthcareProviderId, provider),
            this.getCurrentDashboardRole(), 'client_plans')) {
            throw new Error('Client plans are not available for this healthcare provider or role.');
        }
        return {
            providerId: access.scope.healthcareProviderId,
            ...(Array.isArray(provider.speechNeeds) ? { speechNeedIds: this.clientPlanDocumentIds(provider.speechNeeds) } : {}),
        };
    }

    // Catalogue relationships must resolve to single document IDs, never nested Firestore paths.
    private clientPlanDocumentIds(value: unknown): string[] {
        if (!Array.isArray(value)) return [];
        return Array.from(new Set(value.filter((id): id is string => typeof id === 'string')
            .map((id) => id.trim()).filter((id) => id.length > 0 && id.length <= 200 && !id.includes('/'))));
    }

    // Reject stale sessions before reading content or assigning to a previously selected provider.
    private requireClientPlanAccess(clientId: string): ClientPlanAccess {
        this.requireClientsMutation();
        if (typeof clientId !== 'string' || !clientId.trim() || clientId.includes('/')) {
            throw new Error('Select a valid app client before customising a plan.');
        }
        const scope = this.captureScopedAccess(() => this.canReadClientPlans());
        const userId = this.user?.authenticatedUser?.uid;
        if (!scope) {
            throw new Error('Client plans are not available for this healthcare provider or role.');
        }
        if (!userId || auth.currentUser?.uid !== userId) {
            throw new Error('Please sign in again before customising a plan.');
        }
        return { scope, userId };
    }

    // Recheck the identity and Client plans feature after every network boundary.
    private assertClientPlanAccess(access: ClientPlanAccess): void {
        if (!this.isScopedAccessCurrent(access.scope, () => this.canReadClientPlans()) ||
            this.user?.authenticatedUser?.uid !== access.userId || auth.currentUser?.uid !== access.userId) {
            throw new Error('Your client access changed. Refresh the page and try again.');
        }
    }

    // Fresh parent records prevent a cached Client or provider transfer from authorising a write.
    private requirePlanAppClient(client: Record<string, unknown> | null, access: ClientPlanAccess): void {
        if (!client) {
            throw new Error('This client no longer exists. Refresh the client list and try again.');
        }
        const providerIds = [client.providerId, client.healthcareProviderId, client.healthcareProvider]
            .filter((id) => id !== undefined && id !== '');
        if (!providerIds.length || providerIds.some((id) => id !== access.scope.healthcareProviderId)) {
            throw new Error('The client belongs to another healthcare provider.');
        }
        if (client.userType !== 'carer' && client.userType !== 'care_recipient') {
            throw new Error('A custom app plan can only be assigned to a carer or care recipient client.');
        }
    }

    // Match the deployed override contract before starting a bounded set of topic reads.
    private validateClientPlanGoalIds(goalIds: unknown): asserts goalIds is string[] {
        if (!Array.isArray(goalIds) || goalIds.length < 1 || goalIds.length > CLIENT_PLAN_MAX_TOPICS) {
            throw new Error(`Choose between 1 and ${CLIENT_PLAN_MAX_TOPICS} topics for the custom plan.`);
        }
        if (goalIds.some((id) => typeof id !== 'string' || !id.trim() || id.length > 200 || id.includes('/')) ||
            new Set(goalIds).size !== goalIds.length) {
            throw new Error('The custom plan must contain distinct, valid topics.');
        }
    }

    // Drop visible content and invalidate queued callbacks when its access scope changes.
    private clearClientContentSubscription() {
        const subscription = this.clientContentSubscription;
        this.clientContentSubscription = null;
        if (subscription) {
            subscription.version += 1;
            subscription.unsubscribe?.();
            subscription.onChange({ content: null, loading: false });
        }
    }

    // Reconcile live access independently of unrelated provider refresh generations.
    private async startClientContentSubscription(): Promise<void> {
        const subscription = this.clientContentSubscription;
        if (!subscription) {
            return;
        }
        if (!this.canReadClientPlans() || subscription.providerId !== this.healthcareProviderId ||
            subscription.userId !== this.user?.authenticatedUser?.uid) {
            this.clearClientContentSubscription();
            return;
        }
        if (subscription.unsubscribe) {
            const client = this.clients.find((candidate) => candidate.id === subscription.clientId);
            if (client && ((client.userType !== 'carer' && client.userType !== 'care_recipient') || client.providerId !== subscription.providerId)) {
                this.clearClientContentSubscription();
            }
            return;
        }
        const version = ++subscription.version;
        subscription.onChange({ content: null, loading: true });
        const isCurrent = () => this.clientContentSubscription === subscription &&
            subscription.version === version && subscription.providerId === this.healthcareProviderId &&
            subscription.userId === this.user?.authenticatedUser?.uid && this.canReadClientPlans();
        try {
            const client = await this.requireSelectedClient(subscription.clientId);
            if (!isCurrent()) {
                return;
            }
            if (client?.userType !== 'carer' && client?.userType !== 'care_recipient') {
                this.clearClientContentSubscription();
                return;
            }
            subscription.unsubscribe = onSnapshot(
                doc(db, TABLE_NAME_CLIENTS, subscription.clientId, 'Content', 'current'),
                (snapshot) => {
                    if (!isCurrent()) {
                        return;
                    }
                    try {
                        const content = snapshot.exists() ? parseClientContent(snapshot.data(), subscription.clientId) : null;
                        subscription.onChange({ content, loading: false });
                    } catch (error) {
                        subscription.onChange({ content: null, loading: false, error: (error as Error).message });
                    }
                },
                (error) => {
                    if (isCurrent()) {
                        // Keep the transport reason for support without logging Client content or identifiers.
                        console.warn('DATA: App content read failed.', { code: error?.code || 'unknown' });
                        subscription.onChange({ content: null, loading: false, error: formatClientContentError(error) });
                    }
                }
            );
        } catch (error) {
            if (isCurrent()) {
                subscription.onChange({ content: null, loading: false, error: formatClientContentError(error) });
            }
        }
    }

    // Subscribe only to the open Client, never to every briefing in the provider.
    subscribeClientBriefing(clientId: string, onChange: (state: ClientBriefingState) => void): () => void {
        this.clearClientBriefingSubscription();
        const subscription = {
            clientId,
            providerId: this.healthcareProviderId,
            userId: this.user?.authenticatedUser?.uid || '',
            version: 0,
            onChange,
        };
        this.clientBriefingSubscription = subscription;
        void this.startClientBriefingSubscription();
        return () => {
            if (this.clientBriefingSubscription === subscription) {
                this.clearClientBriefingSubscription();
            }
        };
    }

    // Clear clinical text immediately when the selected record, identity, or access changes.
    private clearClientBriefingSubscription() {
        const subscription = this.clientBriefingSubscription;
        this.clientBriefingSubscription = null;
        if (subscription) {
            subscription.version += 1;
            subscription.unsubscribe?.();
            subscription.onChange({ briefing: null, loading: false });
        }
    }

    // Revalidate access without restarting the open Client's listener for unrelated metadata refreshes.
    private async startClientBriefingSubscription(): Promise<void> {
        const subscription = this.clientBriefingSubscription;
        if (!subscription) {
            return;
        }
        const access = this.captureScopedAccess(() => this.canReadAudioScribeAndReports());
        if (!access || subscription.providerId !== this.healthcareProviderId ||
            subscription.userId !== this.user?.authenticatedUser?.uid) {
            this.clearClientBriefingSubscription();
            return;
        }
        // Firestore already delivers briefing changes; keep its listener and the visible text in place.
        if (subscription.unsubscribe) {
            const client = this.clients.find((candidate) => candidate.id === subscription.clientId);
            if (client && (client.userType !== 'care_recipient' || client.providerId !== subscription.providerId)) {
                this.clearClientBriefingSubscription();
            }
            return;
        }
        const version = ++subscription.version;
        subscription.onChange({ briefing: null, loading: true });
        // A continuous listener follows live identity and permissions, not one-off fetch generations.
        const isCurrent = () => this.clientBriefingSubscription === subscription &&
            subscription.version === version &&
            subscription.providerId === this.healthcareProviderId &&
            subscription.userId === this.user?.authenticatedUser?.uid &&
            this.canReadAudioScribeAndReports();
        try {
            const client = await this.requireSelectedClient(subscription.clientId);
            if (!isCurrent()) {
                return;
            }
            if (client?.userType !== 'care_recipient') {
                this.clearClientBriefingSubscription();
                return;
            }
            subscription.unsubscribe = onSnapshot(doc(db, 'ClientBriefings', subscription.clientId), (snapshot) => {
                if (!isCurrent()) {
                    return;
                }
                const briefing = snapshot.exists() ? snapshot.data() as ClientBriefing : null;
                if (briefing && (briefing.clientId !== subscription.clientId ||
                    briefing.healthcareProviderId !== access.healthcareProviderId ||
                    briefing.providerId !== access.healthcareProviderId)) {
                    subscription.onChange({ briefing: null, loading: false, error: 'The summary is unavailable for this Client.' });
                    return;
                }
                subscription.onChange({ briefing, loading: false });
            }, () => {
                if (isCurrent()) {
                    subscription.onChange({ briefing: null, loading: false, error: 'Could not load the Client summary. Please refresh and try again.' });
                }
            });
        } catch (error) {
            if (isCurrent()) {
                subscription.onChange({ briefing: null, loading: false, error: error instanceof Error ? error.message : 'Could not load the Client summary.' });
            }
        }
    }

    // Queue server-owned generation using the existing server-side OpenAI configuration.
    async requestClientBriefing(clientId: string, force = false): Promise<{ status: string }> {
        const access = this.captureScopedAccess(() => this.canReadAudioScribeAndReports());
        if (!access) {
            throw new Error('Client summaries are not available for your role.');
        }
        const client = await this.requireSelectedClient(clientId);
        if (!this.isScopedAccessCurrent(access, () => this.canReadAudioScribeAndReports()) ||
            client?.userType !== 'care_recipient') {
            throw new Error('Client summaries are only available for care recipients with current access.');
        }
        const request = httpsCallable<{ clientId: string; force: boolean }, { status: string }>(
            getFunctions(), 'requestClientBriefing'
        );
        const result = await request({ clientId, force });
        if (!this.isScopedAccessCurrent(access, () => this.canReadAudioScribeAndReports())) {
            throw new Error('Your Client access changed. Please reopen the record.');
        }
        return result.data;
    }

    // Access checks intentionally require both Firebase authentication and a matching Therapist record.
    private isAuthenticatedDashboardUser(): boolean {
        const authenticatedUserId = this.user?.authenticatedUser?.uid;
        const authenticatedEmail = (this.user?.authenticatedUser?.email || '').trim().toLowerCase();
        const therapistEmail = (this.therapistForCurrentUser.email || '').trim().toLowerCase();
        return !!authenticatedUserId && (
            this.therapistForCurrentUser.id === authenticatedUserId ||
            (this.isCurrentUserMicrosoft() && !!authenticatedEmail && authenticatedEmail === therapistEmail)
        );
    }

    // Email-resolved Therapist records are reserved for Microsoft-authenticated users.
    private isCurrentUserMicrosoft(): boolean {
        return this.user?.authenticatedUser?.emailVerified === true &&
            !!this.user.authenticatedUser.providerData.some(
                (provider) => provider?.providerId === 'microsoft.com'
            );
    }

    private getCurrentDashboardRole(): DashboardRole {
        return getDashboardRole(this.therapistForCurrentUser);
    }

    private getSelectedHealthcareProvider(): HealthcareProvider | undefined {
        return this.healthcareProviders.find((provider) => provider.id === this.healthcareProviderId);
    }

    // Non-platform users can never cross their Therapist record's provider boundary.
    private canAccessSelectedHealthcareProvider(): boolean {
        if (!this.isAuthenticatedDashboardUser() || !this.healthcareProviderId) {
            return false;
        }

        const role = this.getCurrentDashboardRole();
        return isRootOrSuperRole(role) ||
            this.therapistForCurrentUser.healthcareProviderId === this.healthcareProviderId;
    }

    private hasSelectedProviderFeature(feature: DashboardFeature): boolean {
        const provider = this.getSelectedHealthcareProvider();
        return this.canAccessSelectedHealthcareProvider() &&
            !!provider &&
            hasDashboardFeature(provider, this.getCurrentDashboardRole(), feature);
    }

    private canReadAllHealthcareProviders(): boolean {
        return isRootOrSuperRole(this.getCurrentDashboardRole()) &&
            this.hasSelectedProviderFeature('provider_selector');
    }

    private canReadClients(): boolean {
        return this.hasSelectedProviderFeature('clients');
    }

    // Viewing and changing app plans always require both the Client page and its plan feature.
    private canReadClientPlans(): boolean {
        return this.canReadClients() && this.hasSelectedProviderFeature('client_plans');
    }

    private canReadAudioScribeAndReports(): boolean {
        return this.canReadClients() && this.hasSelectedProviderFeature('audio_scribe_reports');
    }

    private canReadMessaging(): boolean {
        return this.hasSelectedProviderFeature('messaging');
    }

    private canReadCampaignsForProvider(healthcareProviderId: string): boolean {
        if (!this.canReadMessaging()) {
            return false;
        }
        if (healthcareProviderId === this.healthcareProviderId) {
            return true;
        }

        const role = this.getCurrentDashboardRole();
        const provider = this.healthcareProviders.find((candidateProvider) => candidateProvider.id === healthcareProviderId);
        return isRootOrSuperRole(role) &&
            !!provider &&
            hasDashboardFeature(provider, role, 'messaging');
    }

    private canReadTherapists(): boolean {
        if (!this.canAccessSelectedHealthcareProvider() || !this.getSelectedHealthcareProvider()) {
            return false;
        }

        // Provider availability also limits the directory used by platform administrators.
        return this.canReadClients() || this.canViewSelectedProviderTeam();
    }

    private canViewSelectedProviderTeam(): boolean {
        return canViewProviderTeam(
            this.getSelectedHealthcareProvider(),
            this.getCurrentDashboardRole()
        ) && this.canAccessSelectedHealthcareProvider();
    }

    // Tools contain global exports, so Development alone is deliberately insufficient.
    private canReadGlobalToolsData(): boolean {
        return isRootOrSuperRole(this.getCurrentDashboardRole()) &&
            this.hasSelectedProviderFeature('development');
    }

    // Mutation guards protect stale presenter callbacks after a role/provider switch.
    private requireClientsMutation(providerId?: string) {
        if (!this.canReadClients()) {
            throw new Error('Your role does not have access to client records.');
        }
        if (providerId && providerId !== this.healthcareProviderId) {
            throw new Error('The client record belongs to another healthcare provider.');
        }
    }

    private requireAudioMutation(providerId?: string) {
        if (!this.canReadAudioScribeAndReports()) {
            throw new Error('Your role does not have access to audio scribe and reports.');
        }
        if (providerId && providerId !== this.healthcareProviderId) {
            throw new Error('The audio or report record belongs to another healthcare provider.');
        }
    }

    private requireMessagingMutation(providerId?: string) {
        if (!this.canReadMessaging()) {
            throw new Error('Your role does not have access to messaging.');
        }
        if (providerId && providerId !== this.healthcareProviderId) {
            throw new Error('The messaging record belongs to another healthcare provider.');
        }
    }

    private requireDevelopmentMutation(providerId?: string) {
        if (!this.hasSelectedProviderFeature('development')) {
            throw new Error('Your role does not have access to Development settings.');
        }
        if (providerId && providerId !== this.healthcareProviderId) {
            throw new Error('The settings belong to another healthcare provider.');
        }
    }

    // Newly created clients can be visible before the provider cache's next refresh.
    private async requireSelectedClient(clientId: string | undefined): Promise<Client | undefined> {
        if (!clientId) {
            return undefined;
        }

        const access = this.captureScopedAccess(() => this.canReadClients());
        if (!access) {
            throw new Error('Your role does not have access to client records.');
        }

        let client = this.clients.find((candidate) => candidate.id === clientId);
        if (!client) {
            // Verify stored ownership instead of treating a cache miss as another provider.
            const clientSnapshot = await getDoc(doc(db, TABLE_NAME_CLIENTS, clientId));
            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                throw new Error('Your client access changed. Refresh the page and try again.');
            }
            if (!clientSnapshot.exists()) {
                throw new Error('This client no longer exists. Refresh the client list and try again.');
            }
            client = withClientSpeechNeeds({ ...clientSnapshot.data(), id: clientSnapshot.id } as Client);
        }

        if (client.providerId !== access.healthcareProviderId) {
            throw new Error('The client belongs to another healthcare provider.');
        }
        return client;
    }

    // Canonicalize every recorder participant against the currently loaded provider directory.
    private normalizeTherapySessionParticipants(
        participants: TherapySession['participants']
    ): TherapySession['participants'] {
        if (participants === undefined) {
            return undefined;
        }
        if (!Array.isArray(participants) || participants.length > 100) {
            throw new Error('A session can contain at most 100 participants.');
        }

        const clientsById = new Map(
            this.clients
                .filter((client): client is Client & {id: string} => !!client.id)
                .map((client) => [client.id, client])
        );
        const participantsByKey = new Map<string, NonNullable<TherapySession['participants']>[number]>();
        participants.forEach((participant) => {
            if (!participant || typeof participant.personId !== 'string') {
                throw new Error('A session participant is invalid.');
            }

            const personId = participant.personId.trim();
            if (participant.personType === 'client') {
                const client = clientsById.get(personId);
                if (!client || client.providerId !== this.healthcareProviderId) {
                    throw new Error('A selected Client belongs to another healthcare provider.');
                }
                participantsByKey.set(`client:${personId}`, {
                    personId,
                    personType: 'client',
                    displayName: this.formatClientName(client) || client.email || 'Unnamed client',
                    ...(client.userType ? {userType: client.userType} : {}),
                });
                return;
            }

            if (participant.personType === 'therapist') {
                const therapist = this.requireSelectedTeamMember(personId);
                participantsByKey.set(`therapist:${personId}`, {
                    personId,
                    personType: 'therapist',
                    displayName: therapist.name || therapist.email || 'Unnamed team member',
                });
                return;
            }

            throw new Error('A session participant type is invalid.');
        });

        return Array.from(participantsByKey.values());
    }

    // Only use the selected provider's directory or the signed-in member as team targets.
    private requireSelectedTeamMember(therapistId: string): Therapist {
        const therapist = this.therapistForCurrentUser.id === therapistId &&
            this.therapistForCurrentUser.healthcareProviderId === this.healthcareProviderId ?
            this.therapistForCurrentUser : this.therapists.find((member) => member.id === therapistId);
        if (!therapist || therapist.healthcareProviderId !== this.healthcareProviderId) {
            throw new Error('A selected Team member belongs to another healthcare provider.');
        }
        return therapist;
    }

    // Resolve every timeline owner before persisting notes or uploading files.
    private async normalizeTimelineDocumentOwnership(
        documentData: Omit<TimelineDocument, 'id' | 'kind'>,
        providerId: string
    ): Promise<Pick<TimelineDocument, 'clientId' | 'clientName' | 'therapistId' | 'therapistName' | 'therapistIds' | 'participants'>> {
        const access = this.captureScopedAccess(() => this.canReadClients());
        const client = await this.requireSelectedClient(documentData.clientId);
        this.requireClientsMutation(client?.providerId || providerId);
        if (!access || !this.isScopedAccessCurrent(access, () => this.canReadClients())) {
            throw new Error('Your timeline access changed. Refresh the page and try again.');
        }
        if (documentData.participants !== undefined &&
            (!Array.isArray(documentData.participants) || documentData.participants.length > 100)) {
            throw new Error('A timeline document can contain at most 100 participants.');
        }
        if (documentData.therapistIds !== undefined && !Array.isArray(documentData.therapistIds)) {
            throw new Error('A selected Team member is invalid.');
        }

        const participantsByKey = new Map<string, NonNullable<TimelineDocument['participants']>[number]>();
        if (client?.id) {
            participantsByKey.set(`client:${client.id}`, {
                personId: client.id,
                personType: 'client',
                displayName: this.formatClientName(client) || client.email || 'Unnamed client',
                ...(client.userType ? {userType: client.userType} : {}),
            });
        }

        // Resolve Client participants separately so uncached, newly created Clients still work.
        const therapistIds = new Set<string>();
        for (const therapistId of [documentData.therapistId, ...(documentData.therapistIds || [])]) {
            if (therapistId !== undefined) {
                if (typeof therapistId !== 'string' || !therapistId.trim()) {
                    throw new Error('A selected Team member is invalid.');
                }
                therapistIds.add(therapistId.trim());
            }
        }
        for (const participant of documentData.participants || []) {
            if (!participant || typeof participant.personId !== 'string' || !participant.personId.trim()) {
                throw new Error('A timeline participant is invalid.');
            }
            const personId = participant.personId.trim();
            if (participant.personType === 'therapist') {
                therapistIds.add(personId);
            } else if (participant.personType === 'client') {
                const participantClient = await this.requireSelectedClient(personId);
                participantsByKey.set(`client:${personId}`, {
                    personId,
                    personType: 'client',
                    displayName: this.formatClientName(participantClient) || participantClient?.email || 'Unnamed client',
                    ...(participantClient?.userType ? {userType: participantClient.userType} : {}),
                });
            } else {
                throw new Error('A timeline participant type is invalid.');
            }
        }
        for (const therapistId of Array.from(therapistIds)) {
            const therapist = this.requireSelectedTeamMember(therapistId);
            participantsByKey.set(`therapist:${therapistId}`, {
                personId: therapistId,
                personType: 'therapist',
                displayName: therapist.name || therapist.email || 'Unnamed team member',
            });
        }

        this.requireClientsMutation(providerId);
        if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
            throw new Error('Your timeline access changed. Refresh the page and try again.');
        }
        const participants = Array.from(participantsByKey.values());
        if (participants.length > 100) {
            throw new Error('A timeline document can contain at most 100 participants.');
        }
        const clientParticipant = participants.find((participant) => participant.personType === 'client');
        const teamParticipant = participants.find((participant) => participant.personType === 'therapist');
        return {
            clientId: clientParticipant?.personId,
            clientName: clientParticipant?.displayName,
            therapistId: teamParticipant?.personId,
            therapistName: teamParticipant?.displayName,
            therapistIds: Array.from(therapistIds),
            participants,
        };
    }

    private requireSelectedTherapySession(sessionId: string) {
        if (!this.therapySessions.some((session) => session.id === sessionId)) {
            throw new Error('The therapy session belongs to another healthcare provider.');
        }
    }

    private requireSelectedTherapySessionReport(reportId: string) {
        if (!this.therapySessionReports.some((report) => report.id === reportId)) {
            throw new Error('The report belongs to another healthcare provider.');
        }
    }

    private requireSelectedTimelineDocument(documentData: Partial<Pick<TimelineDocument, 'id' | 'providerId' | 'healthcareProviderId'>>) {
        const providerId = documentData.healthcareProviderId || documentData.providerId;
        this.requireClientsMutation(providerId);
        if (documentData.id && !this.timelineDocuments.some((timelineDocument) => timelineDocument.id === documentData.id)) {
            throw new Error('The timeline document belongs to another healthcare provider.');
        }
    }

    private async requireSelectedFamilyClients(family: Family) {
        const clientIds = Array.from(new Set([
            family.careRecipient,
            ...(family.members || []).map((member) => member.userId),
        ].filter((clientId): clientId is string => !!clientId)));
        const access = this.captureScopedAccess(() => this.canReadClients());
        if (!access) {
            throw new Error('Your role does not have access to client families.');
        }

        const clientSnapshots = await Promise.all(clientIds.map((clientId) =>
            getDoc(doc(db, TABLE_NAME_CLIENTS, clientId))
        ));
        if (!this.isScopedAccessCurrent(access, () => this.canReadClients()) ||
            clientSnapshots.some((clientSnapshot) =>
                !clientSnapshot.exists() || clientSnapshot.data().providerId !== access.healthcareProviderId
            )) {
            throw new Error('Every family member must belong to the selected healthcare provider.');
        }
    }

    private captureScopedAccess(canRead: () => boolean): ScopedAccessSnapshot | null {
        if (!canRead()) {
            return null;
        }

        return {
            generation: this.scopedDataGeneration,
            healthcareProviderId: this.healthcareProviderId,
        };
    }

    private isScopedAccessCurrent(
        access: ScopedAccessSnapshot,
        canRead: () => boolean
    ): boolean {
        return access.generation === this.scopedDataGeneration &&
            access.healthcareProviderId === this.healthcareProviderId &&
            canRead();
    }

    private clearClientsState() {
        this.clearClientContentSubscription();
        this.clearClientBriefingSubscription();
        this.allClients = [];
        this.clients = [];
        this.clientActivityByClientId = {};
        this.clientActivityTimelineByClientId = {};
        this.clientSessionMinutesTimelineByClientId = {};
        this.clientSignUpTimeByClientId = {};
        this.clientStageByClientId = {};
        this.clientMobileAppUsageByClientId = {};
        this.clientMobileAppUsageStatus = 'loading';
        this.clientsQueryable?.setState({allClients: [], clients: []});
        this.clientActivityQueryable?.setState({
            activityByClientId: {},
            activityTimelineByClientId: {},
            sessionMinutesTimelineByClientId: {},
            signUpTimeByClientId: {},
            stageByClientId: {},
            mobileAppUsageByClientId: {},
            mobileAppUsageStatus: this.clientMobileAppUsageStatus,
        });
    }

    private clearAudioScribeAndReportsState() {
        this.clearClientBriefingSubscription();
        this.therapySessionsFetchRequestId += 1;
        this.therapySessionsFetchesInFlight.clear();
        this.clearSessionPlacedTagEdits();
        this.therapySessions = [];
        this.therapySessionReports = [];
        this.therapySessionsQueryable?.setState({sessions: []});
        this.therapySessionReportsQueryable?.setState({reports: []});
        this.therapySessionReportPollingIds.clear();
        this.therapySessionReportPollingAttemptsById = {};
        if (this.therapySessionReportPollingTimerId) {
            clearTimeout(this.therapySessionReportPollingTimerId);
            this.therapySessionReportPollingTimerId = null;
        }
    }

    private clearTimelineDocumentsState() {
        this.timelineDocuments = [];
        this.timelineDocumentsQueryable?.setState({documents: []});
    }

    private clearTherapistsState() {
        this.therapistsFetchRequestId += 1;
        this.therapists = [];
        this.therapistsIncludeTeamRoleFields = false;
        this.therapistsInCurrentHealthcareProviderQueryable?.setState({therapists: []});
    }

    private getTherapistForSharedCache(therapist: Therapist): Therapist {
        // Client assignment needs only a directory entry; Team viewers additionally see role fields.
        const directoryTherapist: Therapist = {
            id: therapist.id,
            name: therapist.name,
            healthcareProviderId: therapist.healthcareProviderId,
        };
        return this.canViewSelectedProviderTeam() ? {
            ...directoryTherapist,
            // Only Team managers receive the server-validated address used for reply routing.
            ...(this.hasSelectedProviderFeature('organisation_team_manage') && therapist.email?.trim() ?
                { email: therapist.email.trim() } : {}),
            super: therapist.super,
            role: getDashboardRole(therapist),
            signInMethod: therapist.signInMethod,
            emailVerified: therapist.emailVerified,
            emailVerificationRequired: therapist.emailVerificationRequired,
            verificationEmailSentAt: therapist.verificationEmailSentAt,
            billingRole: therapist.billingRole,
            membershipStatus: therapist.membershipStatus,
            suspendedReason: therapist.suspendedReason,
            seatConsumes: therapist.seatConsumes,
        } : directoryTherapist;
    }

    private sanitizeTherapistsForCurrentAccess() {
        this.therapistsFetchRequestId += 1;
        this.therapists = this.therapists.map((therapist) => this.getTherapistForSharedCache(therapist));
        this.therapistsIncludeTeamRoleFields = this.therapistsIncludeTeamRoleFields && this.canViewSelectedProviderTeam();
        this.therapistsInCurrentHealthcareProviderQueryable?.setState({therapists: this.therapists});
    }

    private clearMessagingState() {
        this.commsCampaigns = [];
        this.commsCampaignsQueryable?.setState({campaigns: []});
    }

    private clearUserResponseKeysState() {
        this.userResponseKeys = [];
        this.userResponseKeysQueryable?.setState({keys: []});
    }

    // Re-evaluate every provider-scoped cache whenever identity, role, provider, or access changes.
    private reconcileScopedDataAccess() {
        this.scopedDataGeneration += 1;
        this.reconcileSessionPlacedTagEdits();
        void this.startClientContentSubscription();
        void this.startClientBriefingSubscription();

        if (!this.canReadClients()) {
            this.clearClientsState();
            this.clearTimelineDocumentsState();
        }
        if (!this.canReadAudioScribeAndReports()) {
            this.clearAudioScribeAndReportsState();
        }
        if (!this.canReadMessaging()) {
            this.clearMessagingState();
        }
        if (!this.canReadTherapists()) {
            this.clearTherapistsState();
        } else if (!this.canViewSelectedProviderTeam()) {
            // Drop cached role fields synchronously when Team-view access is revoked.
            this.sanitizeTherapistsForCurrentAccess();
        } else if (!this.therapistsIncludeTeamRoleFields) {
            // Do not render role-sensitive actions from a Clients-only directory snapshot.
            this.clearTherapistsState();
        } else if (!this.hasSelectedProviderFeature('organisation_team_manage')) {
            // Remove reply addresses immediately even when the member retains Team-view access.
            this.sanitizeTherapistsForCurrentAccess();
        }
        if (!this.canReadGlobalToolsData()) {
            this.clearUserResponseKeysState();
        }

        this.restartClientsRefreshPolling();

        if (this.canReadClients()) {
            void this.fetchClients();
            void this.fetchTimelineDocuments();
        }
        if (this.canReadAudioScribeAndReports()) {
            void this.fetchTherapySessions(true);
            void this.fetchTherapySessionReports();
        }
        if (this.canReadMessaging()) {
            void this.fetchCommsCampaigns();
        }
        if (this.canReadTherapists()) {
            void this.fetchTherapistsInCurrentHealthcareProvider();
        }
        if (this.canReadGlobalToolsData()) {
            void this.fetchUserResponseKeys();
        }
    }

    private stripUndefinedFields<T extends Record<string, any>>(value: T): Partial<T> {
        return Object.fromEntries(
            Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined)
        ) as Partial<T>;
    }

    private getCareRecipientDuplicateKeys(client: Client): string[] {
        if (!client.id || !isCareRecipientClient(client)) {
            return [];
        }

        const identityKey = getCareRecipientIdentityKey(client);
        if (!identityKey) {
            return [];
        }

        return getCareRecipientCarerIds(client).map((carerId) => `${carerId}::${identityKey}`);
    }

    private getCareRecipientDuplicateGroups(clients: Client[]): Client[][] {
        const clientById = new Map<string, Client>();
        const parentById = new Map<string, string>();
        const idsByDuplicateKey = new Map<string, string[]>();

        const findRoot = (clientId: string): string => {
            const parentId = parentById.get(clientId);
            if (!parentId || parentId === clientId) {
                return clientId;
            }

            const rootId = findRoot(parentId);
            parentById.set(clientId, rootId);
            return rootId;
        };

        const union = (a: string, b: string) => {
            const rootA = findRoot(a);
            const rootB = findRoot(b);
            if (rootA !== rootB) {
                parentById.set(rootB, rootA);
            }
        };

        clients.forEach((client) => {
            if (!client.id) {
                return;
            }

            const duplicateKeys = this.getCareRecipientDuplicateKeys(client);
            if (duplicateKeys.length === 0) {
                return;
            }

            clientById.set(client.id, client);
            parentById.set(client.id, client.id);
            duplicateKeys.forEach((duplicateKey) => {
                const ids = idsByDuplicateKey.get(duplicateKey) || [];
                ids.push(client.id!);
                idsByDuplicateKey.set(duplicateKey, ids);
            });
        });

        idsByDuplicateKey.forEach((clientIds) => {
            if (clientIds.length < 2) {
                return;
            }

            const [firstClientId, ...otherClientIds] = clientIds;
            otherClientIds.forEach((clientId) => union(firstClientId, clientId));
        });

        const groupsByRootId = new Map<string, Client[]>();
        clientById.forEach((client, clientId) => {
            const rootId = findRoot(clientId);
            const group = groupsByRootId.get(rootId) || [];
            group.push(client);
            groupsByRootId.set(rootId, group);
        });

        return Array.from(groupsByRootId.values()).filter((group) => group.length > 1);
    }

    private chooseCanonicalCareRecipient(clients: Client[]): Client {
        return [...clients].sort((a, b) => {
            const aHasAuthUser = Boolean(a.userId) || a.id === a.userId;
            const bHasAuthUser = Boolean(b.userId) || b.id === b.userId;
            if (aHasAuthUser !== bHasAuthUser) {
                return aHasAuthUser ? -1 : 1;
            }

            const createdAtDifference = getTimestampMillis(a.createdAt) - getTimestampMillis(b.createdAt);
            if (createdAtDifference !== 0) {
                return createdAtDifference;
            }

            return (a.id || '').localeCompare(b.id || '');
        })[0];
    }

    private mergeStringArrays(...values: Array<string[] | undefined>): string[] {
        return Array.from(new Set(
            values
                .flatMap((value) => Array.isArray(value) ? value : [])
                .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        ));
    }

    private mergeCareRecipientClients(canonical: Client, clients: Client[]): Client {
        const merged: Record<string, any> = { ...canonical };

        clients.forEach((client) => {
            Object.entries(client).forEach(([key, value]) => {
                if (['id', 'friendlyId', 'createdAt', 'lastSignInTime'].includes(key)) {
                    return;
                }

                if (key === 'notes' && typeof value === 'string' && value.trim()) {
                    const existingNotes = typeof merged.notes === 'string' ? merged.notes.trim() : '';
                    if (!existingNotes) {
                        merged.notes = value;
                    } else if (!existingNotes.includes(value.trim())) {
                        merged.notes = `${existingNotes}\n\n${value.trim()}`;
                    }
                    return;
                }

                const existingValue = merged[key];
                const isExistingEmpty = existingValue === undefined || existingValue === null || existingValue === '';
                if (isExistingEmpty && value !== undefined && value !== null && value !== '') {
                    merged[key] = value;
                }
            });
        });

        const speechNeeds = this.mergeStringArrays(...clients.map((client) => getClientSpeechNeeds(client)));
        const conditionIds = this.mergeStringArrays(...clients.map((client) => client.conditionIds));
        const carers = this.mergeStringArrays(...clients.map((client) => client.carers));

        merged.userType = 'care_recipient';
        merged.carers = carers;
        merged.speechNeeds = speechNeeds;
        merged.speechNeed = speechNeeds[0] || '';
        if (conditionIds.length > 0) {
            merged.conditionIds = conditionIds;
        }

        return merged as Client;
    }

    private async fetchProviderClientsFromFirestore(healthcareProviderId: string): Promise<Client[]> {
        if (!this.canReadClients() || healthcareProviderId !== this.healthcareProviderId) {
            return [];
        }

        const queryProviderClients = query(
            collection(db, TABLE_NAME_CLIENTS),
            where('providerId', '==', healthcareProviderId)
        );
        const providerClientsSnapshot = await getDocs(queryProviderClients);
        return providerClientsSnapshot.docs.map(doc => withClientSpeechNeeds({ ...doc.data(), id: doc.id } as Client));
    }

    // Query provider-linked records without downloading an entire cross-provider collection.
    private async fetchProviderLinkedDocuments(
        tableName: string,
        healthcareProviderId: string,
        clientIds: Array<string>,
        canRead: () => boolean
    ): Promise<Array<{ id: string; data: Record<string, any> }>> {
        if (healthcareProviderId !== this.healthcareProviderId || !canRead()) {
            return [];
        }

        const fetchLinkedDocuments = httpsCallable<
            { healthcareProviderId: string; collectionName: string; clientIds: string[] },
            {
                healthcareProviderId: string;
                collectionName: string;
                documents: Array<{ id: string; data: Record<string, any> }>;
            }
        >(getFunctions(), 'fetchProviderLinkedDocuments');
        const result = await fetchLinkedDocuments({
            healthcareProviderId,
            collectionName: tableName,
            clientIds,
        });
        if (healthcareProviderId !== this.healthcareProviderId || !canRead()) {
            return [];
        }
        if (result.data.healthcareProviderId !== healthcareProviderId || result.data.collectionName !== tableName) {
            throw new Error('Provider-linked records were returned for the wrong scope.');
        }
        // Older deployed callables expose Admin SDK timestamps as private JSON fields.
        return result.data.documents.map((documentRecord) => ({
            ...documentRecord,
            data: reviveCallableFirestoreTimestamps(documentRecord.data),
        }));
    }

    private async findExistingCareRecipientsForClient(client: Client): Promise<Client[]> {
        const identityKey = getCareRecipientIdentityKey(client);
        const carerIds = getCareRecipientCarerIds(client);
        if (!identityKey || carerIds.length === 0) {
            return [];
        }

        const matchesById = new Map<string, Client>();
        for (const carerId of carerIds) {
            const clientsRef = collection(db, TABLE_NAME_CLIENTS);
            const matchingCarerQuery = query(
                clientsRef,
                where('providerId', '==', this.healthcareProviderId),
                where('carers', 'array-contains', carerId)
            );
            const snapshot = await getDocs(matchingCarerQuery);
            snapshot.docs.forEach((clientDoc) => {
                const existingClient = withClientSpeechNeeds({ ...clientDoc.data(), id: clientDoc.id } as Client);
                if (isCareRecipientClient(existingClient) && careRecipientsMatchIdentity(existingClient, client)) {
                    matchesById.set(clientDoc.id, existingClient);
                }
            });
        }

        return Array.from(matchesById.values());
    }

    private async coalesceCareRecipientGroup(existingClients: Client[], incomingClient?: Client): Promise<string> {
        if (existingClients.length === 0) {
            throw new Error('Cannot coalesce care recipients without an existing client.');
        }

        const canonicalClient = this.chooseCanonicalCareRecipient(existingClients);
        const clientsToMerge = incomingClient ? [...existingClients, incomingClient] : existingClients;
        const mergedClient = this.mergeCareRecipientClients(canonicalClient, clientsToMerge);
        const canonicalId = canonicalClient.id!;
        const duplicateIds = existingClients
            .map((client) => client.id)
            .filter((clientId): clientId is string => Boolean(clientId && clientId !== canonicalId));

        const { id: _omittedClientId, ...clientWithoutId } = withClientSpeechNeeds(mergedClient);
        const mergeCareRecipients = httpsCallable<
            {
                healthcareProviderId: string;
                canonicalClientId: string;
                duplicateClientIds: string[];
                mergedClient: Record<string, any>;
            },
            {
                healthcareProviderId: string;
                canonicalClientId: string;
                duplicateClientIds: string[];
                merged: true;
            }
        >(getFunctions(), 'coalesceProviderCareRecipients');
        const result = await mergeCareRecipients({
            healthcareProviderId: this.healthcareProviderId,
            canonicalClientId: canonicalId,
            duplicateClientIds: duplicateIds,
            mergedClient: this.stripUndefinedFields(clientWithoutId) as Record<string, any>,
        });
        if (!result.data.merged || result.data.healthcareProviderId !== this.healthcareProviderId ||
            result.data.canonicalClientId !== canonicalId) {
            throw new Error('Care-recipient coalescing completed with an unexpected provider scope.');
        }

        return canonicalId;
    }

    private async coalesceDetectedCareRecipientDuplicates(clients: Client[]): Promise<number> {
        const duplicateGroups = this.getCareRecipientDuplicateGroups(clients);
        let coalescedCount = 0;

        for (const duplicateGroup of duplicateGroups) {
            await this.coalesceCareRecipientGroup(duplicateGroup);
            coalescedCount += duplicateGroup.length - 1;
        }

        return coalescedCount;
    }

    private sanitizeStorageFileName(fileName: string): string {
        // Keep uploaded timeline document paths provider-safe while preserving useful filename text.
        return fileName
            .replace(/[^a-zA-Z0-9._-]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '') || 'document';
    }

    private getActivityDateKey(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Parse Firestore payload values into millisecond timestamps when present.
    private parseActivityMs(value: unknown): number | null {
        const parsedValue = typeof value === 'number' ? value : Number(value);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    // Extract a best-effort session start/end window from an app_session event.
    private getAppSessionWindow(data: unknown): { startedAtMs: number; endedAtMs: number } | null {
        if (!data || typeof data !== 'object') {
            return null;
        }

        const sessionData = data as Record<string, unknown>;
        const startedAtMs = this.parseActivityMs(sessionData.startedAtMs);
        const endedAtMs = this.parseActivityMs(sessionData.endedAtMs) ??
            this.parseActivityMs(sessionData.lastSeenAtMs);
        const durationMs = this.parseActivityMs(sessionData.durationMs);

        if (startedAtMs != null && endedAtMs != null && endedAtMs > startedAtMs) {
            return { startedAtMs, endedAtMs };
        }

        if (startedAtMs != null && durationMs != null && durationMs > 0) {
            return {
                startedAtMs,
                endedAtMs: startedAtMs + durationMs,
            };
        }

        return null;
    }

    // Split session duration over local calendar days so per-day charts stay accurate.
    private addSessionDurationToTimeline(
        sessionDurationMsByDate: Map<string, number>,
        startedAtMs: number,
        endedAtMs: number
    ) {
        let cursorMs = startedAtMs;

        while (cursorMs < endedAtMs) {
            const cursorDate = new Date(cursorMs);
            const nextDayStart = new Date(cursorDate);
            nextDayStart.setHours(24, 0, 0, 0);

            const segmentEndMs = Math.min(endedAtMs, nextDayStart.getTime());
            const activityDateKey = this.getActivityDateKey(cursorDate);
            const currentDurationMs = sessionDurationMsByDate.get(activityDateKey) || 0;

            sessionDurationMsByDate.set(
                activityDateKey,
                currentDurationMs + (segmentEndMs - cursorMs)
            );

            cursorMs = segmentEndMs;
        }
    }

    private getActivityEventDate(
        activityEvents: Array<{ type: string; timestamp: Date }>,
        matchingTypes: Array<string>,
        mode: 'first' | 'last'
    ): Date | null {
        const filteredEvents = activityEvents
            .filter((event) => matchingTypes.includes(event.type))
            .map((event) => event.timestamp)
            .filter((date) => !isNaN(date.getTime()))
            .sort((leftDate, rightDate) => leftDate.getTime() - rightDate.getTime());

        if (filteredEvents.length === 0) {
            return null;
        }

        return mode === 'first' ? filteredEvents[0] : filteredEvents[filteredEvents.length - 1];
    }

    private getClientStage(
        client: Client | undefined,
        activityEvents: Array<{ type: string; timestamp: Date }>
    ): CommsCampaignStage {
        const hasSignedIn = !!client?.lastSignInTime || !!this.getActivityEventDate(activityEvents, ['user_signed_in'], 'first');
        if (!hasSignedIn) {
            return 'referral_created_not_signed_in';
        }

        const firstUsedAppDate = this.getActivityEventDate(activityEvents, ['goal_completed'], 'first');
        if (firstUsedAppDate) {
            return 'first_used_app';
        }

        const onboardingCompletedDate = this.getActivityEventDate(activityEvents, ['onboarding_completed', 'completed_onboarding'], 'first');
        if (onboardingCompletedDate) {
            return 'completed_onboarding';
        }

        return 'signed_in_successfully';
    }

    constructor(options: {app: App}) {
        console.log('DATA: constructor()');
        this.app = options.app;
    }

    start() {
        console.log('DATA: start()');
        this.conditionsQueryable = newQueryable({conditions: this.conditions, selectedCondition: this.selectedCondition}, 'CONDITIONS');
        this.fetchConditions();

        this.currentTherapistQueryable = newQueryable(this.therapistForCurrentUser, 'USER_THERAPIST');
        this.therapistsInCurrentHealthcareProviderQueryable = newQueryable({therapists: this.therapists}, 'THERAPISTS');
        // fetch later, when the healthcare provider is set

        this.speechNeedsQueryable = newQueryable({speechNeeds: this.speechNeeds}, 'SPEECH_NEEDS');
        this.allSpeechNeedsQueryable = newQueryable({speechNeeds: this.speechNeeds}, 'ALL_SPEECH_NEEDS');
        this.fetchSpeechNeeds();

        this.healthcareProvidersQueryable = newQueryable({healthcareProviders: this.healthcareProviders}, 'HEALTHCARE_PROVIDERS');
        this.healthcareProviderInfoQueryable = newQueryable(this.healthcareProviderInfo, 'HEALTHCARE_PROVIDER_INFO');
        this.commsCampaignsQueryable = newQueryable({campaigns: this.commsCampaigns}, 'COMMS_CAMPAIGNS');
        this.userResponseKeysQueryable = newQueryable({keys: this.userResponseKeys}, 'USER_RESPONSE_KEYS');

        this.clientsQueryable = newQueryable({allClients: this.allClients, clients: this.clients}, 'CLIENTS');
        this.therapySessionsQueryable = newQueryable({sessions: this.therapySessions}, 'THERAPY_SESSIONS');
        this.therapySessionReportsQueryable = newQueryable({reports: this.therapySessionReports}, 'THERAPY_SESSION_REPORTS');
        this.timelineDocumentsQueryable = newQueryable({documents: this.timelineDocuments}, 'TIMELINE_DOCUMENTS');
        this.clientActivityQueryable = newQueryable<ClientActivityState>({
            activityByClientId: this.clientActivityByClientId,
            activityTimelineByClientId: this.clientActivityTimelineByClientId,
            sessionMinutesTimelineByClientId: this.clientSessionMinutesTimelineByClientId,
            signUpTimeByClientId: this.clientSignUpTimeByClientId,
            stageByClientId: this.clientStageByClientId,
            mobileAppUsageByClientId: this.clientMobileAppUsageByClientId,
            mobileAppUsageStatus: this.clientMobileAppUsageStatus,
        }, 'CLIENT_ACTIVITY');
        // fetch later, when the healthcare provider is set

        this.observeUser();
    }
    
    private observeUser() {
        this.app.queryUser().observe({
            changed: (user: User) => {
                console.log('DATA: queryUser().observe()', user);
                this.user = user;

                if (!this.isAuthenticatedDashboardUser()) {
                    // A logout or unresolved Therapist record invalidates every cached provider value.
                    this.healthcareProvidersFetchGeneration += 1;
                    this.healthcareProviderSubscriptionGeneration += 1;
                    this.currentHealthcareProviderUnsubscribe?.();
                    this.currentHealthcareProviderUnsubscribe = null;
                    this.healthcareProviderId = '';
                    this.healthcareProviders = [];
                    this.healthcareProvidersQueryable.setState({healthcareProviders: []});
                    this.setHealthcareProviderInfo(undefined);
                    this.reconcileScopedDataAccess();
                    return;
                }

                // Role changes can alter both the visible provider list and every scoped collection.
                this.subscribeToCurrentHealthcareProvider();
                void this.fetchHealthcareProviders();
                this.reconcileScopedDataAccess();
            }
        });
    }

    private parseHealthcareProvider(id: string, providerData: Record<string, any>): HealthcareProvider {
        const knownFeatureIds = new Set(DASHBOARD_FEATURES.map((feature) => feature.id));
        const rawAllowedFeatures = Array.isArray(providerData.selfServiceAllowedDashboardFeatures) ?
            providerData.selfServiceAllowedDashboardFeatures :
            Array.isArray(providerData.allowedDashboardFeatures) ? providerData.allowedDashboardFeatures : undefined;
        const allowedDashboardFeatures = rawAllowedFeatures?.filter(
            (feature: unknown): feature is DashboardFeature =>
                typeof feature === 'string' && knownFeatureIds.has(feature as DashboardFeature)
        );
        return {
            id,
            path: typeof providerData.path === 'string' ? providerData.path : undefined,
            name: providerData.name,
            commercialModel: providerData.commercialModel === 'self_service' ? 'self_service' :
                providerData.commercialModel === 'custom_enterprise' ? 'custom_enterprise' : undefined,
            selfServicePlanCode: providerData.selfServicePlanCode,
            allowedDashboardFeatures,
            licenseStatus: providerData.licenseStatus,
            inviteCodes: providerData.inviteCodes,
            dashboardUrl: providerData.dashboardUrl,
            heatmapUrl: providerData.heatmapUrl,
            licenses: providerData.licenses,
            logoUrl: providerData.logoUrl,
            messagingReplyToTherapistId: typeof providerData.messagingReplyToTherapistId === 'string' ?
                providerData.messagingReplyToTherapistId : undefined,
            // Preserve explicit [] while ignoring malformed legacy values that would break filtering.
            speechNeeds: Array.isArray(providerData.speechNeeds) ? Array.from(new Set(
                providerData.speechNeeds.filter((speechNeedId: unknown): speechNeedId is string =>
                    typeof speechNeedId === 'string' && speechNeedId.length > 0)
            )) : undefined,
            postcodesAccess: providerData.postcodesAccess || [],
            reportTemplates: providerData.reportTemplates || [],
            dashboardFeatureAccess: providerData.dashboardFeatureAccess,
            // Retain only known boolean switches; absent switches preserve existing availability.
            dashboardFeatureAvailability: Object.fromEntries(CONFIGURABLE_DASHBOARD_FEATURES
                .filter((feature) => typeof providerData.dashboardFeatureAvailability?.[feature.id] === 'boolean')
                .map((feature) => [feature.id, providerData.dashboardFeatureAvailability[feature.id]])),
            dashboardCustomRoles: providerData.dashboardCustomRoles || [],
        };
    }

    /** Preserve runtime billing data without preserving a stale SaaS feature ceiling. */
    private mergeHealthcareProviderRuntimeState(
        provider: HealthcareProvider,
        previousProvider?: HealthcareProvider
    ): HealthcareProvider {
        if (!previousProvider) return provider;
        const entitlement = previousProvider.entitlement ? {
            ...previousProvider.entitlement,
            allowedDashboardFeatures: provider.commercialModel === 'self_service' &&
                Array.isArray(provider.allowedDashboardFeatures) ?
                provider.allowedDashboardFeatures : previousProvider.entitlement.allowedDashboardFeatures,
        } : undefined;
        return {
            ...provider,
            entitlement,
            licenseUsage: previousProvider.licenseUsage,
        };
    }

    private setHealthcareProviderInfo(healthcareProvider?: HealthcareProvider) {
        this.healthcareProviderInfo = healthcareProvider ? {
            id: healthcareProvider.id,
            path: healthcareProvider.path,
            name: healthcareProvider.name,
            commercialModel: healthcareProvider.commercialModel,
            selfServicePlanCode: healthcareProvider.selfServicePlanCode,
            entitlement: healthcareProvider.entitlement,
            licenseUsage: healthcareProvider.licenseUsage,
            allowedDashboardFeatures: healthcareProvider.allowedDashboardFeatures,
            licenseStatus: healthcareProvider.licenseStatus,
            dashboardUrl: healthcareProvider.dashboardUrl,
            heatmapUrl: healthcareProvider.heatmapUrl,
            inviteCodes: healthcareProvider.inviteCodes,
            licenses: healthcareProvider.licenses,
            logoUrl: healthcareProvider.logoUrl,
            messagingReplyToTherapistId: healthcareProvider.messagingReplyToTherapistId,
            speechNeeds: healthcareProvider.speechNeeds,
            postcodesAccess: healthcareProvider.postcodesAccess || [],
            reportTemplates: healthcareProvider.reportTemplates || [],
            dashboardFeatureAccess: healthcareProvider.dashboardFeatureAccess,
            dashboardFeatureAvailability: healthcareProvider.dashboardFeatureAvailability,
            dashboardCustomRoles: healthcareProvider.dashboardCustomRoles || [],
        } : {
            id: '',
            path: undefined,
            name: '',
            commercialModel: undefined,
            selfServicePlanCode: undefined,
            entitlement: undefined,
            licenseUsage: undefined,
            allowedDashboardFeatures: undefined,
            licenseStatus: undefined,
            dashboardUrl: '',
            heatmapUrl: undefined,
            inviteCodes: [],
            licenses: undefined,
            speechNeeds: undefined,
            postcodesAccess: [],
            reportTemplates: [],
            dashboardFeatureAccess: undefined,
            dashboardFeatureAvailability: undefined,
            dashboardCustomRoles: [],
        };
        this.healthcareProviderInfoQueryable.setState(this.healthcareProviderInfo);
    }

    // Load the server-owned SaaS ceiling and usage without exposing billing records to Firestore clients.
    private async refreshSelfServiceEntitlement(healthcareProviderId: string): Promise<void> {
        const provider = this.healthcareProviders.find((candidate) => candidate.id === healthcareProviderId);
        if (!provider || provider.commercialModel !== 'self_service' || !this.isAuthenticatedDashboardUser()) {
            return;
        }

        const fetchGeneration = ++this.selfServiceEntitlementFetchGeneration;
        try {
            const getUsage = httpsCallable<
                { healthcareProviderId: string },
                Record<string, any>
            >(getFunctions(), 'getProviderLicenseUsage');
            const result = await getUsage({ healthcareProviderId });
            if (fetchGeneration !== this.selfServiceEntitlementFetchGeneration ||
                this.healthcareProviderId !== healthcareProviderId) {
                return;
            }

            const value = result.data || {};
            const knownFeatureIds = new Set(DASHBOARD_FEATURES.map((feature) => feature.id));
            const allowedDashboardFeatures = Array.isArray(value.allowedDashboardFeatures) ?
                value.allowedDashboardFeatures.filter((feature: unknown): feature is DashboardFeature =>
                    typeof feature === 'string' && knownFeatureIds.has(feature as DashboardFeature)
                ) : [];
            const licenseStatus = value.licenseStatus === 'overage' || value.licenseStatus === 'exhausted' ?
                value.licenseStatus : 'available';
            const entitlement: ProviderEntitlementSnapshot = {
                planCode: value.planCode || provider.selfServicePlanCode || 'starter',
                planName: value.planName,
                ...parseProviderBillingFields(value),
                status: value.entitlementStatus || 'free',
                serviceAccessStatus: value.serviceAccessStatus,
                serviceAccessReason: value.serviceAccessReason,
                regularisationDueAt: value.regularisationDueAt,
                billingOwnerUserId: value.billingOwnerUid,
                allowedDashboardFeatures,
                subscriptionUnits: Number(value.subscriptionUnits) || 1,
                dashboardSeatLimit: Number(value.dashboardSeatLimit) || 1,
                licenseStatus,
                availableLicenses: Number(value.availableLicenses) || 0,
                outstandingOverageLicenses: Number(value.outstandingOverageLicenses) || 0,
            };
            const licenseUsage: LicenseUsageSummary = {
                providerId: healthcareProviderId,
                commercialModel: 'self_service',
                planCode: entitlement.planCode,
                planName: value.planName,
                licenseModel: value.policyModel || value.licenseModel || 'total',
                periodStartsAt: value.periodStartsAt,
                periodEndsAt: value.periodEndsAt,
                numberOfLicensesAvailablePerYear:
                    Number(value.numberOfLicensesAvailablePerYear) || Number(value.includedLicenses) || 0,
                includedLicenses: Number(value.includedLicenses) || 0,
                purchasedLicenses: Number(value.purchasedLicenses) || 0,
                usedLicensesSinceAnnualRenewalDate: Number(value.usedLicensesSinceAnnualRenewalDate) || 0,
                currentlySponsoredFamilies: Number(value.currentlySponsoredFamilies) || 0,
                availableLicenses: Number(value.availableLicenses) || 0,
                outstandingOverageLicenses: Number(value.outstandingOverageLicenses) || 0,
                status: licenseStatus,
                dashboardSeatLimit: entitlement.dashboardSeatLimit,
                dashboardSeatsUsed: Number(value.dashboardSeatsUsed) || 0,
                dashboardSeatsReserved: Number(value.dashboardSeatsReserved) || 0,
                subscriptionUnits: entitlement.subscriptionUnits,
                minimumRequiredSubscriptionUnits: Math.max(
                    1,
                    Number(value.minimumRequiredSubscriptionUnits) || 1
                ),
                audioScribeRecordingsUsed: Number(value.audioScribeRecordingsUsed) || 0,
                audioScribeRecordingsLimit: value.audioScribeRecordingsLimit === null ||
                    value.audioScribeRecordingsLimit === undefined ? null : Number(value.audioScribeRecordingsLimit) || 0,
                audioScribeRecordingsRemaining: value.audioScribeRecordingsRemaining === null ||
                    value.audioScribeRecordingsRemaining === undefined ? null : Number(value.audioScribeRecordingsRemaining) || 0,
                aiGeneratedReportsUsed: Number(value.aiGeneratedReportsUsed) || 0,
                aiGeneratedReportsLimit: value.aiGeneratedReportsLimit === null ||
                    value.aiGeneratedReportsLimit === undefined ? null : Number(value.aiGeneratedReportsLimit) || 0,
                aiGeneratedReportsRemaining: value.aiGeneratedReportsRemaining === null ||
                    value.aiGeneratedReportsRemaining === undefined ? null : Number(value.aiGeneratedReportsRemaining) || 0,
                overageGraceEndsAt: value.regularisationDueAt || value.overageGraceEndsAt,
            };

            // Usage totals can change without invalidating in-flight provider data or pending edits.
            const previousFeatureAccess = DASHBOARD_FEATURES.map((feature) =>
                this.hasSelectedProviderFeature(feature.id)
            );
            this.healthcareProviders = this.healthcareProviders.map((candidate) =>
                candidate.id === healthcareProviderId ? {
                    ...candidate,
                    selfServicePlanCode: entitlement.planCode,
                    allowedDashboardFeatures,
                    entitlement,
                    licenseUsage,
                    licenseStatus,
                } : candidate
            );
            this.healthcareProvidersQueryable.setState({ healthcareProviders: this.healthcareProviders });
            this.setHealthcareProviderInfo(this.healthcareProviders.find((candidate) =>
                candidate.id === healthcareProviderId
            ));
            if (DASHBOARD_FEATURES.some((feature, index) =>
                this.hasSelectedProviderFeature(feature.id) !== previousFeatureAccess[index]
            )) {
                this.reconcileScopedDataAccess();
            }
        } catch (error) {
            // SaaS providers fail closed on paid features until authoritative usage is available.
            console.error('DATA: refreshSelfServiceEntitlement(): Could not load entitlement:', error);
        }
    }

    private subscribeToCurrentHealthcareProvider() {
        const subscriptionGeneration = ++this.healthcareProviderSubscriptionGeneration;
        this.currentHealthcareProviderUnsubscribe?.();
        this.currentHealthcareProviderUnsubscribe = null;

        const healthcareProviderId = this.healthcareProviderId;
        if (!healthcareProviderId || !this.canAccessSelectedHealthcareProvider()) {
            return;
        }

        // Keep feature revocations effective without requiring a dashboard reload.
        this.currentHealthcareProviderUnsubscribe = onSnapshot(
            doc(db, TABLE_NAME_HEALTHCARE_PROVIDERS, healthcareProviderId),
            (providerSnapshot) => {
                if (subscriptionGeneration !== this.healthcareProviderSubscriptionGeneration ||
                    this.healthcareProviderId !== healthcareProviderId) {
                    return;
                }
                this.healthcareProvidersFetchGeneration += 1;

                if (!providerSnapshot.exists()) {
                    this.healthcareProviders = this.healthcareProviders.filter((provider) => provider.id !== healthcareProviderId);
                    this.healthcareProvidersQueryable.setState({healthcareProviders: this.healthcareProviders});
                    this.setHealthcareProviderInfo(undefined);
                    this.reconcileScopedDataAccess();
                    return;
                }

                const parsedProvider = this.parseHealthcareProvider(providerSnapshot.id, providerSnapshot.data());
                const healthcareProvider = this.mergeHealthcareProviderRuntimeState(
                    parsedProvider,
                    this.healthcareProviders.find((provider) => provider.id === parsedProvider.id)
                );
                const selectorIsEnabled = isRootOrSuperRole(this.getCurrentDashboardRole()) &&
                    hasDashboardFeature(healthcareProvider, this.getCurrentDashboardRole(), 'provider_selector');
                const existingProviderIndex = this.healthcareProviders.findIndex((provider) => provider.id === healthcareProviderId);
                if (!selectorIsEnabled) {
                    // Drop other provider metadata immediately when selector access is revoked.
                    this.healthcareProviders = [healthcareProvider];
                } else if (existingProviderIndex >= 0) {
                    this.healthcareProviders = this.healthcareProviders.map((provider) =>
                        provider.id === healthcareProviderId ? healthcareProvider : provider
                    );
                } else {
                    this.healthcareProviders = [...this.healthcareProviders, healthcareProvider];
                }
                this.healthcareProviders.sort((leftProvider, rightProvider) => leftProvider.name.localeCompare(rightProvider.name));
                this.healthcareProvidersQueryable.setState({healthcareProviders: this.healthcareProviders});
                this.setHealthcareProviderInfo(healthcareProvider);
                void this.refreshSelfServiceEntitlement(healthcareProviderId);
                this.filterSpeechNeedsAndNotifyObservers();
                this.reconcileScopedDataAccess();
                if (selectorIsEnabled && this.healthcareProviders.length === 1) {
                    void this.fetchHealthcareProviders();
                }
            },
            (error) => {
                console.error('DATA: subscribeToCurrentHealthcareProvider(): Provider access listener failed:', error);
                if (subscriptionGeneration === this.healthcareProviderSubscriptionGeneration &&
                    this.healthcareProviderId === healthcareProviderId) {
                    // Listener failures fail closed because access may have been revoked.
                    this.healthcareProvidersFetchGeneration += 1;
                    this.healthcareProviders = this.healthcareProviders.filter((provider) => provider.id !== healthcareProviderId);
                    this.healthcareProvidersQueryable.setState({healthcareProviders: this.healthcareProviders});
                    this.setHealthcareProviderInfo(undefined);
                    this.reconcileScopedDataAccess();
                }
            }
        );
    }

    setHealthcareProviderId(healthcareProviderId: string) {
        console.log('DATA: setHealthcareProviderId()', healthcareProviderId);
        const healthcareProviderChanged = this.healthcareProviderId !== healthcareProviderId;
        if (healthcareProviderChanged) {
            // Never leave the previous provider's rows visible while the next provider loads.
            this.clearClientsState();
            this.clearAudioScribeAndReportsState();
            this.clearTimelineDocumentsState();
            this.clearTherapistsState();
            this.clearMessagingState();
            this.clearUserResponseKeysState();
        }
        this.healthcareProviderId = healthcareProviderId;
        this.filterSpeechNeedsAndNotifyObservers();
        const healthcareProvider = this.healthcareProviders.find(provider => provider.id === healthcareProviderId);
        this.setHealthcareProviderInfo(healthcareProvider);
        this.subscribeToCurrentHealthcareProvider();
        this.reconcileScopedDataAccess();
        void this.fetchHealthcareProviders();
        void this.refreshSelfServiceEntitlement(healthcareProviderId);
    }

    private restartClientsRefreshPolling() {
        if (this.clientsRefreshIntervalId) {
            clearInterval(this.clientsRefreshIntervalId);
            this.clientsRefreshIntervalId = null;
        }

        if (!this.canReadClients()) {
            return;
        }

        // Poll client data so the Clients list keeps itself fresh without reloads.
        this.clientsRefreshIntervalId = setInterval(() => {
            void this.fetchClients();
            void this.refreshSelfServiceEntitlement(this.healthcareProviderId);
        }, CLIENTS_REFRESH_INTERVAL_MS);
    }

    queryHealthcareProviderInfo(): Query<HealthcareProviderInfo> {
        return this.healthcareProviderInfoQueryable.query();
    }

    async fetchAssessments(): Promise<Array<AssessmentDefinition>> {
        const access = this.captureScopedAccess(() => this.canReadClients());
        if (!access) {
            throw new Error('Your role does not have access to client assessment data.');
        }

        try {
            const assessmentsSnapshot = await getDocs(collection(db, TABLE_NAME_ASSESSMENTS));
            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return [];
            }

            return assessmentsSnapshot.docs.map((assessmentDoc) => ({
                id: assessmentDoc.id,
                fScore: String(assessmentDoc.data().fScore || ''),
                fToText: typeof assessmentDoc.data().fToText === 'string' ? assessmentDoc.data().fToText : undefined,
                includeInBriefing: assessmentDoc.data().includeInBriefing === true,
                title: String(assessmentDoc.data().title || assessmentDoc.id),
            }));
        } catch (error) {
            console.error('DATA: fetchAssessments(): Error fetching assessment definitions:', error);
            throw error;
        }
    }

    async fetchUserResponsesForClient(client: Client): Promise<Array<UserResponseRecord>> {
        const access = this.captureScopedAccess(() => this.canReadClients());
        const clientBelongsToSelectedProvider = client.providerId === this.healthcareProviderId ||
            this.clients.some((providerClient) => providerClient.id === client.id);
        if (!access || !clientBelongsToSelectedProvider) {
            throw new Error('Your role does not have access to this client.');
        }

        try {
            const candidateUserIds = Array.from(new Set(
                [client.userId, client.id]
                    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
            ));

            if (candidateUserIds.length === 0) {
                return [];
            }

            const userResponsesCollection = collection(db, TABLE_NAME_USER_RESPONSES);
            const userResponsesQuery = candidateUserIds.length === 1
                ? query(userResponsesCollection, where('userId', '==', candidateUserIds[0]))
                : query(userResponsesCollection, where('userId', 'in', candidateUserIds.slice(0, 10)));
            const userResponsesSnapshot = await getDocs(userResponsesQuery);

            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return [];
            }

            return userResponsesSnapshot.docs
                .map((userResponseDoc) => ({
                    date: userResponseDoc.data().date,
                    key: userResponseDoc.data().key,
                    userId: userResponseDoc.data().userId,
                    data: userResponseDoc.data().data,
                }) as UserResponseRecord)
                .sort((leftRecord, rightRecord) => new Date(leftRecord.date).getTime() - new Date(rightRecord.date).getTime());
        } catch (error) {
            console.error('DATA: fetchUserResponsesForClient(): Error fetching client user responses:', error);
            throw error;
        }
    }

    async fetchUserResponsesForTools(filters: { healthcareProviderId?: string; key?: string }): Promise<Array<UserResponseRecord>> {
        const access = this.captureScopedAccess(() => this.canReadGlobalToolsData());
        if (!access) {
            throw new Error('Only Root or Super users with Development access can export response data.');
        }

        try {
            const userResponsesCollection = collection(db, TABLE_NAME_USER_RESPONSES);
            const userResponsesQuery = filters.key
                ? query(userResponsesCollection, where('key', '==', filters.key))
                : userResponsesCollection;
            const userResponsesSnapshot = await getDocs(userResponsesQuery);

            if (!this.isScopedAccessCurrent(access, () => this.canReadGlobalToolsData())) {
                return [];
            }

            // Export needs limited identity metadata even when the provider's Clients page is disabled.
            type ExportClient = Pick<Client, 'id' | 'userId' | 'providerId' | 'carers' | 'speechNeed' | 'speechNeeds'>;
            const getExportClients = httpsCallable<
                {healthcareProviderId?: string}, {clients: Array<ExportClient>}
            >(getFunctions(), 'getUserResponseExportClients');
            const clientsResult = await getExportClients(filters.healthcareProviderId
                ? {healthcareProviderId: filters.healthcareProviderId} : {});

            if (!this.isScopedAccessCurrent(access, () => this.canReadGlobalToolsData())) {
                return [];
            }

            const clients = clientsResult.data.clients;
            const clientsById = new Map<string, ExportClient>();
            const providerIdsByUserId = new Map<string, string>();
            const speechNeedsByUserId = new Map<string, Set<string>>();

            clients.forEach((client) => {
                const ids = [client.userId, client.id].filter((value): value is string => typeof value === 'string' && value.trim() !== '');
                ids.forEach((id) => clientsById.set(id, client));
            });

            let allowedUserIds: Set<string> | undefined;
            const userIdsFromClients = clients.flatMap((client) => {
                const providerId = client.providerId;
                const ids = [client.userId, client.id].filter((value): value is string => typeof value === 'string' && value.trim() !== '');

                // Index both auth user id and Client doc id because historical responses can reference either.
                if (typeof providerId === 'string' && providerId.trim() !== '') {
                    ids.forEach((id) => {
                        if (!providerIdsByUserId.has(id)) {
                            providerIdsByUserId.set(id, providerId);
                        }
                    });
                }

                return ids;
            });

            // Index care-recipient speech needs by both carer Client id and carer auth user id.
            clients.forEach((client) => {
                const carerIds = Array.isArray(client.carers)
                    ? client.carers.filter((value): value is string => typeof value === 'string' && value.trim() !== '')
                    : [];
                const speechNeedIds = getAllClientSpeechNeedIds(client);

                carerIds.forEach((carerId) => {
                    addSpeechNeedsForExportUser(speechNeedsByUserId, carerId, speechNeedIds);
                    addSpeechNeedsForExportUser(speechNeedsByUserId, clientsById.get(carerId)?.userId, speechNeedIds);
                });
            });

            if (filters.healthcareProviderId) {
                allowedUserIds = new Set(userIdsFromClients);
            }

            return userResponsesSnapshot.docs
                .map((userResponseDoc) => ({
                    date: userResponseDoc.data().date,
                    key: userResponseDoc.data().key,
                    userId: userResponseDoc.data().userId,
                    providerId: providerIdsByUserId.get(userResponseDoc.data().userId),
                    speechNeeds: Array.from(speechNeedsByUserId.get(userResponseDoc.data().userId) || []),
                    data: userResponseDoc.data().data,
                }) as UserResponseRecord)
                .filter((record) => !allowedUserIds || allowedUserIds.has(record.userId));
        } catch (error) {
            console.error('DATA: fetchUserResponsesForTools(): Error fetching UserResponses export:', error);
            throw error;
        }
    }

    async updateHealthcareProvider(healthcareProvider: HealthcareProvider): Promise<void> {
        this.requireDevelopmentMutation(healthcareProvider.id);
        try {
            console.log('DATA: updateHealthcareProvider(): Updating healthcare provider:', healthcareProvider);

            // Get reference to the healthcare provider document
            const healthcareProviderRef = doc(db, 'HealthcareProviders', healthcareProvider.id);
            
            // Update the document in Firebase (excluding id since it's the document ID)
            await updateDoc(healthcareProviderRef, this.stripUndefinedFields({
                name: healthcareProvider.name,
                path: healthcareProvider.path,
                inviteCodes: healthcareProvider.inviteCodes,
                dashboardUrl: healthcareProvider.dashboardUrl,
                postcodesAccess: healthcareProvider.postcodesAccess,
                reportTemplates: healthcareProvider.reportTemplates,
            }));
            
            // Update local state
            const providerIndex = this.healthcareProviders.findIndex(p => p.id === healthcareProvider.id);
            if (providerIndex !== -1) {
                const previousProvider = this.healthcareProviders[providerIndex];
                const nextProvider = {
                    ...previousProvider,
                    ...healthcareProvider,
                    reportTemplates: healthcareProvider.reportTemplates ?? previousProvider.reportTemplates,
                };
                this.healthcareProviders[providerIndex] = nextProvider;
                
                // Sort providers alphabetically by name to maintain consistency
                this.healthcareProviders.sort((a, b) => a.name.localeCompare(b.name));
                
                // Notify observers
                this.healthcareProvidersQueryable.setState({healthcareProviders: this.healthcareProviders});
                
                // Update healthcare provider info if this is the currently selected provider
                if (this.healthcareProviderId === healthcareProvider.id) {
                    this.healthcareProviderInfo = {
                        id: nextProvider.id,
                        path: nextProvider.path,
                        name: nextProvider.name,
                        commercialModel: nextProvider.commercialModel,
                        selfServicePlanCode: nextProvider.selfServicePlanCode,
                        entitlement: nextProvider.entitlement,
                        licenseUsage: nextProvider.licenseUsage,
                        allowedDashboardFeatures: nextProvider.allowedDashboardFeatures,
                        licenseStatus: nextProvider.licenseStatus,
                        inviteCodes: nextProvider.inviteCodes,
                        dashboardUrl: nextProvider.dashboardUrl,
                        heatmapUrl: nextProvider.heatmapUrl,
                        licenses: nextProvider.licenses,
                        logoUrl: nextProvider.logoUrl,
                        messagingReplyToTherapistId: nextProvider.messagingReplyToTherapistId,
                        speechNeeds: nextProvider.speechNeeds,
                        postcodesAccess: nextProvider.postcodesAccess || [],
                        reportTemplates: nextProvider.reportTemplates || [],
                        dashboardFeatureAccess: nextProvider.dashboardFeatureAccess,
                        dashboardFeatureAvailability: nextProvider.dashboardFeatureAvailability,
                        dashboardCustomRoles: nextProvider.dashboardCustomRoles || [],
                    };
                    this.healthcareProviderInfoQueryable.setState(this.healthcareProviderInfo);
                }
            }
            
            console.log('DATA: updateHealthcareProvider(): Successfully updated healthcare provider:', healthcareProvider.id);
        } catch (error) {
            console.error('DATA: updateHealthcareProvider(): Error updating healthcare provider:', error);
            throw error;
        }
    }

    async updateHealthcareProviderProfile(
        healthcareProviderId: string,
        updates: HealthcareProviderProfileUpdate
    ): Promise<HealthcareProviderProfile> {
        // Snapshot the selected provider and entitlement so stale callbacks cannot update another clinic's cache.
        const access = this.captureScopedAccess(() => this.hasSelectedProviderFeature('organisation_team_manage'));
        const name = updates.name.trim();
        if (!access || healthcareProviderId !== access.healthcareProviderId || !name) {
            throw new Error('Your role cannot edit this organisation profile.');
        }

        const updateProfile = httpsCallable<
            HealthcareProviderProfileUpdate & { healthcareProviderId: string },
            HealthcareProviderProfile
        >(getFunctions(), 'updateHealthcareProviderProfile');
        // Build a sparse payload because Firebase callable serialization can turn undefined into null.
        const request: HealthcareProviderProfileUpdate & { healthcareProviderId: string } = {
            healthcareProviderId,
            name,
        };
        if (updates.logo) {
            request.logo = updates.logo;
        }
        if (updates.removeLogo === true) {
            request.removeLogo = true;
        }
        const result = await updateProfile(request);
        const profile = result.data;
        if (profile.healthcareProviderId !== healthcareProviderId || !profile.name?.trim() ||
            (profile.logoUrl !== null && typeof profile.logoUrl !== 'string')) {
            throw new Error('The organisation profile update returned an unexpected result.');
        }

        if (!this.isScopedAccessCurrent(
            access,
            () => this.hasSelectedProviderFeature('organisation_team_manage')
        )) {
            return profile;
        }

        // The callable response is authoritative, including an explicit null when the logo was removed.
        const logoUrl = profile.logoUrl || undefined;
        this.healthcareProviders = this.healthcareProviders.map((provider) =>
            provider.id === healthcareProviderId ? {
                ...provider,
                name: profile.name,
                logoUrl,
            } : provider
        ).sort((leftProvider, rightProvider) => leftProvider.name.localeCompare(rightProvider.name));
        this.healthcareProvidersQueryable.setState({ healthcareProviders: this.healthcareProviders });

        this.healthcareProviderInfo = {
            ...this.healthcareProviderInfo,
            name: profile.name,
            logoUrl,
        };
        this.healthcareProviderInfoQueryable.setState(this.healthcareProviderInfo);
        return profile;
    }

    async setHealthcareProviderMessagingReplyTo(
        healthcareProviderId: string,
        therapistId: string
    ): Promise<HealthcareProviderMessagingReplyTo> {
        // Bind the change to the current Team-management grant and a member with a known email.
        const access = this.captureScopedAccess(() => this.hasSelectedProviderFeature('organisation_team_manage'));
        const therapist = this.therapists.find((candidate) => candidate.id === therapistId);
        if (!access || healthcareProviderId !== access.healthcareProviderId ||
            !therapistId?.trim() || therapistId.includes('/') || !therapist ||
            therapist.healthcareProviderId !== healthcareProviderId || !therapist.email?.trim()) {
            throw new Error('Your role cannot set this organisation\'s Messaging reply-to contact.');
        }

        const setReplyTo = httpsCallable<
            { healthcareProviderId: string; therapistId: string },
            HealthcareProviderMessagingReplyTo
        >(getFunctions(), 'setHealthcareProviderMessagingReplyTo');
        const result = await setReplyTo({ healthcareProviderId, therapistId });
        const replyTo = result.data;
        if (!replyTo || replyTo.healthcareProviderId !== healthcareProviderId ||
            replyTo.therapistId !== therapistId || typeof replyTo.email !== 'string' ||
            !/^[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+$/.test(replyTo.email)) {
            throw new Error('The Messaging reply-to update returned an unexpected result.');
        }
        if (!this.isScopedAccessCurrent(
            access,
            () => this.hasSelectedProviderFeature('organisation_team_manage')
        )) {
            return replyTo;
        }

        // Keep only the member ID in provider caches; the scheduler resolves the current email.
        this.healthcareProviders = this.healthcareProviders.map((provider) =>
            provider.id === healthcareProviderId ? {
                ...provider,
                messagingReplyToTherapistId: replyTo.therapistId,
            } : provider
        );
        this.healthcareProvidersQueryable.setState({ healthcareProviders: this.healthcareProviders });
        this.healthcareProviderInfo = {
            ...this.healthcareProviderInfo,
            messagingReplyToTherapistId: replyTo.therapistId,
        };
        this.healthcareProviderInfoQueryable.setState(this.healthcareProviderInfo);
        return replyTo;
    }

    async updateHealthcareProviderSpeechNeeds(healthcareProviderId: string, speechNeeds: Array<string> | undefined): Promise<void> {
        // Persist the provider's support-area access list; undefined removes the
        // restriction so the provider is allowed every speech need, including future ones.
        this.requireDevelopmentMutation(healthcareProviderId);
        try {
            const healthcareProviderRef = doc(db, TABLE_NAME_HEALTHCARE_PROVIDERS, healthcareProviderId);
            await updateDoc(healthcareProviderRef, { speechNeeds: speechNeeds ?? deleteField() });

            this.healthcareProviders = this.healthcareProviders.map((provider) =>
                provider.id === healthcareProviderId ? { ...provider, speechNeeds } : provider
            );
            this.healthcareProvidersQueryable.setState({healthcareProviders: this.healthcareProviders});

            if (this.healthcareProviderId === healthcareProviderId) {
                this.healthcareProviderInfo = {
                    ...this.healthcareProviderInfo,
                    speechNeeds,
                };
                this.healthcareProviderInfoQueryable.setState(this.healthcareProviderInfo);
            }

            // Re-filter so the rest of the dashboard immediately offers the updated support areas.
            this.filterSpeechNeedsAndNotifyObservers();
        } catch (error) {
            console.error('DATA: updateHealthcareProviderSpeechNeeds(): Error updating provider support areas:', error);
            throw error;
        }
    }

    async updateHealthcareProviderReportTemplates(
        healthcareProviderId: string,
        reportTemplates: Array<ReportTemplate>
    ): Promise<void> {
        // Persist provider report template metadata without disturbing other provider fields.
        this.requireAudioMutation(healthcareProviderId);
        try {
            const healthcareProviderRef = doc(db, 'HealthcareProviders', healthcareProviderId);
            await updateDoc(healthcareProviderRef, { reportTemplates });

            this.healthcareProviders = this.healthcareProviders.map((provider) =>
                provider.id === healthcareProviderId ? { ...provider, reportTemplates } : provider
            );
            this.healthcareProvidersQueryable.setState({healthcareProviders: this.healthcareProviders});

            if (this.healthcareProviderId === healthcareProviderId) {
                this.healthcareProviderInfo = {
                    ...this.healthcareProviderInfo,
                    reportTemplates,
                };
                this.healthcareProviderInfoQueryable.setState(this.healthcareProviderInfo);
            }
        } catch (error) {
            console.error('DATA: updateHealthcareProviderReportTemplates(): Error updating report templates:', error);
            throw error;
        }
    }

    // Save one platform-controlled switch and ignore responses from an obsolete provider/session.
    async updateHealthcareProviderFeatureAvailability(
        healthcareProviderId: string,
        feature: DashboardFeature,
        enabled: boolean
    ): Promise<void> {
        const canConfigure = () => this.canAccessSelectedHealthcareProvider() &&
            isRootOrSuperRole(this.getCurrentDashboardRole()) && !!this.getSelectedHealthcareProvider();
        const access = this.captureScopedAccess(canConfigure);
        if (!access || healthcareProviderId !== access.healthcareProviderId ||
            !CONFIGURABLE_DASHBOARD_FEATURES.some((candidate) => candidate.id === feature) ||
            typeof enabled !== 'boolean') {
            throw new Error('Your role cannot configure this provider\'s feature availability.');
        }
        const updateAvailability = httpsCallable<
            { healthcareProviderId: string; feature: DashboardFeature; enabled: boolean },
            { healthcareProviderId: string; dashboardFeatureAvailability: DashboardFeatureAccess }
        >(getFunctions(), 'updateHealthcareProviderFeatureAvailability');
        const { data: result } = await updateAvailability({ healthcareProviderId, feature, enabled });
        if (result?.healthcareProviderId !== healthcareProviderId ||
            result.dashboardFeatureAvailability?.[feature] !== enabled) {
            throw new Error('The feature availability update returned an unexpected result.');
        }
        if (!this.isScopedAccessCurrent(access, canConfigure)) {
            return;
        }

        // Merge only this switch so other live provider changes and saved role permissions survive.
        this.healthcareProviders = this.healthcareProviders.map((provider) =>
            provider.id === healthcareProviderId ? {
                ...provider,
                dashboardFeatureAvailability: { ...provider.dashboardFeatureAvailability, [feature]: enabled },
            } : provider
        );
        this.healthcareProvidersQueryable.setState({ healthcareProviders: this.healthcareProviders });
        this.healthcareProviderInfo = {
            ...this.healthcareProviderInfo,
            dashboardFeatureAvailability: {
                ...this.healthcareProviderInfo.dashboardFeatureAvailability,
                [feature]: enabled,
            },
        };
        this.healthcareProviderInfoQueryable.setState(this.healthcareProviderInfo);
        this.reconcileScopedDataAccess();
    }

    async updateHealthcareProviderDashboardAccess(
        healthcareProviderId: string,
        dashboardFeatureAccess: DashboardFeatureAccessByRole,
        dashboardCustomRoles: Array<DashboardCustomRole>
    ): Promise<void> {
        // The callable repeats this check and caps every SaaS feature at the purchased plan.
        if (healthcareProviderId !== this.healthcareProviderId ||
            !canConfigureProviderDashboardAccess(this.getSelectedHealthcareProvider(), this.getCurrentDashboardRole())) {
            throw new Error('Your role cannot configure the selected provider\'s dashboard access.');
        }
        const functions = getFunctions();
        const updateDashboardAccess = httpsCallable(functions, 'updateHealthcareProviderDashboardAccess');
        await updateDashboardAccess({
            healthcareProviderId,
            dashboardFeatureAccess,
            dashboardCustomRoles,
            dashboardAccessVersion: 2,
        });

        this.healthcareProviders = this.healthcareProviders.map((provider) =>
            provider.id === healthcareProviderId ? {
                ...provider,
                dashboardFeatureAccess,
                dashboardCustomRoles,
            } : provider
        );
        this.healthcareProvidersQueryable.setState({healthcareProviders: this.healthcareProviders});

        if (this.healthcareProviderId === healthcareProviderId) {
            this.healthcareProviderInfo = {
                ...this.healthcareProviderInfo,
                dashboardFeatureAccess,
                dashboardCustomRoles,
            };
            this.healthcareProviderInfoQueryable.setState(this.healthcareProviderInfo);
            this.reconcileScopedDataAccess();
        }
    }

    async updateCommsCampaign(campaign: CommsCampaign): Promise<void> {
        this.requireMessagingMutation(campaign.healthcareProvider);
        try {
            // Persist ids before saving so subsequent edits keep stable message identity.
            const normalizedMessages = ensureCampaignMessagesHaveIds(campaign.messages).messages;
            const normalizedCampaign = {
                ...campaign,
                messages: normalizedMessages,
            };
            console.log('DATA: updateCommsCampaign(): Updating communications campaign:', normalizedCampaign);

            const campaignRef = doc(db, TABLE_NAME_COMMS_CAMPAIGNS, campaign.id);
            await updateDoc(campaignRef, this.stripUndefinedFields({
                healthcareProvider: normalizedCampaign.healthcareProvider,
                messages: normalizedCampaign.messages,
                name: normalizedCampaign.name,
            }));

            const campaignIndex = this.commsCampaigns.findIndex(existingCampaign => existingCampaign.id === campaign.id);
            let nextCampaigns: Array<CommsCampaign>;
            if (campaignIndex !== -1) {
                nextCampaigns = this.commsCampaigns.map((existingCampaign, index) =>
                    index === campaignIndex ? normalizedCampaign : existingCampaign
                );
            } else {
                nextCampaigns = [...this.commsCampaigns, normalizedCampaign];
            }

            nextCampaigns.sort((a, b) => a.name.localeCompare(b.name));
            this.commsCampaigns = nextCampaigns;
            this.commsCampaignsQueryable.setState({campaigns: this.commsCampaigns});

            console.log('DATA: updateCommsCampaign(): Successfully updated communications campaign:', normalizedCampaign.id);
        } catch (error) {
            console.error('DATA: updateCommsCampaign(): Error updating communications campaign:', error);
            throw error;
        }
    }

    // Load campaign documents for any provider, optionally persisting old message migrations.
    private async loadCommsCampaignsForHealthcareProvider(
        healthcareProviderId: string,
        persistMessageMigrations: boolean
    ): Promise<Array<CommsCampaign>> {
        if (!healthcareProviderId || !this.canReadCampaignsForProvider(healthcareProviderId)) {
            return [];
        }

        const queryCommsCampaigns = query(
            collection(db, TABLE_NAME_COMMS_CAMPAIGNS),
            where('healthcareProvider', '==', healthcareProviderId)
        );
        // Only authoritative results may trigger default creation or persisted message migrations.
        const commsCampaignsSnapshot = await getDocsFromServer(queryCommsCampaigns);
        if (!this.canReadCampaignsForProvider(healthcareProviderId)) {
            return [];
        }
        const campaignMigrationPromises: Array<Promise<void>> = [];
        const campaigns = commsCampaignsSnapshot.docs.map((campaignDoc) => {
            const normalizedMessages = ensureCampaignMessagesHaveIds(campaignDoc.data().messages || []);
            if (persistMessageMigrations && normalizedMessages.changed) {
                // Persist ids immediately for the currently selected provider's campaign.
                campaignMigrationPromises.push(updateDoc(campaignDoc.ref, {
                    messages: normalizedMessages.messages,
                }));
            }

            return {
                id: campaignDoc.id,
                healthcareProvider: campaignDoc.data().healthcareProvider,
                messages: normalizedMessages.messages,
                name: campaignDoc.data().name,
            };
        }) as CommsCampaign[];

        if (campaignMigrationPromises.length > 0) {
            await Promise.all(campaignMigrationPromises);
        }

        return campaigns.sort((a, b) => a.name.localeCompare(b.name));
    }

    // Fetch another provider's campaigns without changing the dashboard-selected provider.
    async fetchCommsCampaignsForHealthcareProvider(healthcareProviderId: string): Promise<Array<CommsCampaign>> {
        const access = this.captureScopedAccess(() => this.canReadCampaignsForProvider(healthcareProviderId));
        if (!access) {
            throw new Error('Your role does not have access to this provider\'s messaging data.');
        }

        try {
            const campaigns = await this.loadCommsCampaignsForHealthcareProvider(healthcareProviderId, false);
            return this.isScopedAccessCurrent(access, () => this.canReadCampaignsForProvider(healthcareProviderId))
                ? campaigns
                : [];
        } catch (error) {
            console.error('DATA: fetchCommsCampaignsForHealthcareProvider(): Error fetching communications campaigns:', error);
            throw error;
        }
    }

    private async fetchConditions() {
        const querySnapshot = await getDocs(collection(db, 'Conditions'));
        let defaultConditionId = '';
        querySnapshot.forEach((doc) => {
            if (doc.data().default) {
                defaultConditionId = doc.id;
            }
        });
        this.selectedCondition = defaultConditionId;
        this.conditions = querySnapshot.docs.map(doc => ({ id: doc.id, name: doc.data().en.name, default: doc.data().default }));
        // notify observers
        this.conditionsQueryable.setState({conditions: this.conditions, selectedCondition: this.selectedCondition});
    }

    private async fetchUserResponseKeys() {
        const access = this.captureScopedAccess(() => this.canReadGlobalToolsData());
        if (!access) {
            this.clearUserResponseKeysState();
            return;
        }

        try {
            const querySnapshot = await getDocs(collection(db, TABLE_NAME_USER_RESPONSES));
            if (!this.isScopedAccessCurrent(access, () => this.canReadGlobalToolsData())) {
                return;
            }

            const keys = Array.from(new Set(
                querySnapshot.docs
                    .map(doc => doc.data().key)
                    .filter((key): key is string => typeof key === 'string' && key.trim() !== '')
            )).sort((a, b) => a.localeCompare(b));

            this.userResponseKeys = keys;
            this.userResponseKeysQueryable.setState({keys: this.userResponseKeys});
        } catch (error) {
            console.error('DATA: fetchUserResponseKeys(): Error fetching UserResponses keys:', error);
            if (this.isScopedAccessCurrent(access, () => this.canReadGlobalToolsData())) {
                this.clearUserResponseKeysState();
            }
        }
    }

    private async fetchTherapistsInCurrentHealthcareProvider() {
        console.log('DATA: fetchTherapistsInCurrentHealthcareProvider(): Fetching therapists for healthcare provider: ', this.healthcareProviderId);
        const access = this.captureScopedAccess(() => this.canReadTherapists());
        if (!access) {
            this.clearTherapistsState();
            return;
        }
        const requestId = ++this.therapistsFetchRequestId;

        try {
            const functions = getFunctions();
            const getDirectory = httpsCallable<
                { healthcareProviderId: string },
                { therapists: Array<Therapist>; includesTeamRoles: boolean }
            >(functions, 'getProviderTherapistDirectory');
            const result = await getDirectory({healthcareProviderId: access.healthcareProviderId});
            if (requestId !== this.therapistsFetchRequestId ||
                !this.isScopedAccessCurrent(access, () => this.canReadTherapists())) {
                return;
            }

            const expectsTeamRoles = this.canViewSelectedProviderTeam();
            if (expectsTeamRoles && !result.data.includesTeamRoles) {
                // Local access metadata is ahead of the server; fail closed until it converges.
                this.clearTherapistsState();
                return;
            }
            this.therapists = result.data.therapists.map((therapist) =>
                this.getTherapistForSharedCache(therapist)
            );
            this.therapistsIncludeTeamRoleFields = expectsTeamRoles && result.data.includesTeamRoles;
            console.log('DATA: fetchTherapists(): Therapists: ', this.therapists);
            this.therapistsInCurrentHealthcareProviderQueryable.setState({therapists: this.therapists});
        } catch (error) {
            console.error('DATA: fetchTherapistsInCurrentHealthcareProvider(): Error fetching team:', error);
            if (requestId === this.therapistsFetchRequestId &&
                this.isScopedAccessCurrent(access, () => this.canReadTherapists())) {
                this.clearTherapistsState();
            }
        }
    }

    private filterSpeechNeedsAndNotifyObservers() {
        if (!this.healthcareProviderId) {
            this.speechNeedsQueryable.setState({speechNeeds: []});
            return;
        }
        const healthcareProvider = this.healthcareProviders.find(provider => provider.id === this.healthcareProviderId);
        if (!healthcareProvider) {
            this.speechNeedsQueryable.setState({speechNeeds: []});
            return;
        }
        const filteredSpeechNeeds = speechNeedsVisibleToProvider(this.speechNeeds, healthcareProvider);
        this.speechNeedsQueryable.setState({speechNeeds: filteredSpeechNeeds});
    }

    private async fetchSpeechNeeds() {
        const querySnapshot = await getDocs(collection(db, 'SpeechNeeds'));
        this.speechNeeds = querySnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().en.name,
            type: doc.data().type,
        }));
        this.allSpeechNeedsQueryable.setState({speechNeeds: this.speechNeeds});
        this.filterSpeechNeedsAndNotifyObservers();
    }
    
    private async fetchHealthcareProviders() {
        const fetchGeneration = ++this.healthcareProvidersFetchGeneration;
        const authenticatedUserId = this.user?.authenticatedUser?.uid;
        if (!authenticatedUserId || !this.isAuthenticatedDashboardUser()) {
            return;
        }

        const role = this.getCurrentDashboardRole();
        const homeHealthcareProviderId = this.therapistForCurrentUser.healthcareProviderId;
        const selectedOrHomeHealthcareProviderId = this.healthcareProviderId || homeHealthcareProviderId;
        const fetchAllProviders = this.canReadAllHealthcareProviders();

        try {
            let providers: Array<HealthcareProvider> = [];
            if (fetchAllProviders) {
                const querySnapshot = await getDocs(collection(db, TABLE_NAME_HEALTHCARE_PROVIDERS));
                providers = querySnapshot.docs.map((providerDoc) => {
                    const parsedProvider = this.parseHealthcareProvider(providerDoc.id, providerDoc.data());
                    return this.mergeHealthcareProviderRuntimeState(
                        parsedProvider,
                        this.healthcareProviders.find((provider) => provider.id === parsedProvider.id)
                    );
                });
            } else if (selectedOrHomeHealthcareProviderId) {
                const providerSnapshot = await getDoc(doc(
                    db,
                    TABLE_NAME_HEALTHCARE_PROVIDERS,
                    selectedOrHomeHealthcareProviderId
                ));
                if (providerSnapshot.exists()) {
                    const parsedProvider = this.parseHealthcareProvider(providerSnapshot.id, providerSnapshot.data());
                    providers = [this.mergeHealthcareProviderRuntimeState(
                        parsedProvider,
                        this.healthcareProviders.find((provider) => provider.id === parsedProvider.id)
                    )];
                }
            }

            const requestIsCurrent = fetchGeneration === this.healthcareProvidersFetchGeneration &&
                authenticatedUserId === this.user?.authenticatedUser?.uid &&
                role === this.getCurrentDashboardRole() &&
                this.isAuthenticatedDashboardUser();
            if (!requestIsCurrent) {
                return;
            }

            providers.sort((leftProvider, rightProvider) => leftProvider.name.localeCompare(rightProvider.name));
            this.healthcareProviders = providers;
            console.log('DATA: fetchHealthcareProviders(): HealthcareProviders: ', JSON.stringify(this.healthcareProviders));
            this.healthcareProvidersQueryable.setState({healthcareProviders: this.healthcareProviders});
            this.setHealthcareProviderInfo(this.getSelectedHealthcareProvider());
            void this.refreshSelfServiceEntitlement(this.healthcareProviderId);
            this.filterSpeechNeedsAndNotifyObservers();
            this.reconcileScopedDataAccess();
            if (!fetchAllProviders && this.canReadAllHealthcareProviders()) {
                // Loading the selected provider may reveal selector entitlement for a second, expanded fetch.
                void this.fetchHealthcareProviders();
            }
        } catch (error) {
            console.error('DATA: fetchHealthcareProviders(): Error fetching providers:', error);
            if (fetchGeneration === this.healthcareProvidersFetchGeneration) {
                this.healthcareProviders = [];
                this.healthcareProvidersQueryable.setState({healthcareProviders: []});
                this.setHealthcareProviderInfo(undefined);
                this.reconcileScopedDataAccess();
            }
        }
    }

    // Render the best available person name for session tables.
    private formatClientName(client?: Client): string {
        if (!client) {
            return '';
        }

        if (client.givenName && client.familyName) {
            return `${client.givenName} ${client.familyName}`;
        }

        return client.givenName || client.familyName || client.displayName || '';
    }

    // Pull the first supported client id field from the still-new Sessions shape.
    private getSessionClientId(sessionData: Record<string, any>): string {
        const clientId = sessionData.clientId || sessionData.client || sessionData.clientDocId;
        return typeof clientId === 'string' ? clientId.trim() : '';
    }

    // Read the new participant roster while synthesizing one Client for legacy sessions.
    private getSessionParticipants(
        sessionData: Record<string, any>,
        client?: Client
    ): NonNullable<TherapySession['participants']> {
        if (Array.isArray(sessionData.participants)) {
            return sessionData.participants.flatMap((participant: unknown) => {
                if (!participant || typeof participant !== 'object') {
                    return [];
                }
                const candidate = participant as Record<string, unknown>;
                const personId = typeof candidate.personId === 'string' ? candidate.personId.trim() : '';
                const displayName = typeof candidate.displayName === 'string' ? candidate.displayName.trim() : '';
                if (!personId || !displayName ||
                    (candidate.personType !== 'client' && candidate.personType !== 'therapist')) {
                    return [];
                }

                return [{
                    personId,
                    personType: candidate.personType,
                    displayName,
                    ...(typeof candidate.userType === 'string' ? {userType: candidate.userType} : {}),
                } as NonNullable<TherapySession['participants']>[number]];
            });
        }

        const legacyClientId = this.getSessionClientId(sessionData);
        if (!legacyClientId) {
            return [];
        }
        return [{
            personId: legacyClientId,
            personType: 'client',
            displayName: sessionData.clientName || sessionData.clientDisplayName ||
                this.formatClientName(client) || 'Client',
            userType: client?.userType,
        }];
    }

    // Keep provider filtering resilient while the Sessions document schema settles.
    private getSessionProviderId(sessionData: Record<string, any>, client?: Client): string {
        const providerId = sessionData.providerId ||
            sessionData.healthcareProviderId ||
            sessionData.healthcareProvider ||
            client?.providerId;
        return typeof providerId === 'string' ? providerId : '';
    }

    // Convert Firestore timestamps, ISO strings, or Dates into sortable milliseconds.
    private getTherapySessionDateMs(session: TherapySession): number {
        return getFirstFirestoreTimestampDate(session.date, session.startedAt, session.createdAt)?.getTime() || 0;
    }

    // Pull the current report client id from the report document shape.
    private getReportClientId(reportData: Record<string, any>): string {
        const clientId = reportData.clientId || reportData.client || reportData.clientDocId;
        return typeof clientId === 'string' ? clientId.trim() : '';
    }

    // Keep report provider filtering aligned with the provider-scoped Sessions fetch.
    private getReportProviderId(reportData: Record<string, any>, client?: Client): string {
        const providerId = reportData.providerId ||
            reportData.healthcareProviderId ||
            reportData.healthcareProvider ||
            client?.providerId;
        return typeof providerId === 'string' ? providerId : '';
    }

    // Pull the current timeline document client id from the new mixed timeline shape.
    private getTimelineDocumentClientId(documentData: Record<string, any>): string {
        const clientId = documentData.clientId || documentData.client || documentData.clientDocId;
        return typeof clientId === 'string' ? clientId.trim() : '';
    }

    // Keep timeline document filtering aligned with provider-scoped sessions and reports.
    private getTimelineDocumentProviderId(documentData: Record<string, any>, client?: Client): string {
        const providerId = documentData.providerId ||
            documentData.healthcareProviderId ||
            documentData.healthcareProvider ||
            client?.providerId;
        return typeof providerId === 'string' ? providerId : '';
    }

    // Convert report timestamps into sortable milliseconds.
    private getTherapySessionReportDateMs(report: TherapySessionReport): number {
        return getFirstFirestoreTimestampDate(report.date, report.createdAt, report.updatedAt)?.getTime() || 0;
    }

    // Convert timeline document timestamps into sortable milliseconds.
    private getTimelineDocumentDateMs(document: TimelineDocument): number {
        return getFirstFirestoreTimestampDate(document.date, document.createdAt, document.updatedAt)?.getTime() || 0;
    }

    // Fetch Sessions and enrich each row with provider/client context for display.
    private async fetchTherapySessions(background = false) {
        const access = this.captureScopedAccess(() => this.canReadAudioScribeAndReports());
        if (!access) {
            this.clearAudioScribeAndReportsState();
            return;
        }

        // Repeated polling must let the active read finish; mutations still request fresh data.
        const fetchScopeKey = `${access.generation}:${access.healthcareProviderId}`;
        if (background && this.therapySessionsFetchesInFlight.has(fetchScopeKey)) {
            return;
        }

        // A refresh begun before an edit cannot replace that edit with an older server snapshot.
        const requestId = ++this.therapySessionsFetchRequestId;
        this.therapySessionsFetchesInFlight.set(fetchScopeKey, requestId);
        const tagEditsAtFetchStart = new Map(Array.from(this.sessionPlacedTagEdits, ([id, state]) => [id, {
            state,
            revision: state.revision,
            pending: state.pending.length > 0,
        }]));

        try {
            const clientsById = new Map(
                [...this.allClients, ...this.clients]
                    .filter((client): client is Client & { id: string } => !!client.id)
                    .map((client) => [client.id, client])
            );
            const providerClientIds = this.clients
                .map((client) => client.id)
                .filter((clientId): clientId is string => !!clientId);
            const sessionDocuments = await this.fetchProviderLinkedDocuments(
                TABLE_NAME_SESSIONS,
                access.healthcareProviderId,
                providerClientIds,
                () => this.canReadAudioScribeAndReports()
            );
            if (requestId !== this.therapySessionsFetchRequestId ||
                !this.isScopedAccessCurrent(access, () => this.canReadAudioScribeAndReports())) {
                return;
            }

            type ParsedTherapySession = TherapySession & { inferredProviderId: string };
            const sessions = sessionDocuments
                .map(({ id, data: sessionData }): ParsedTherapySession => {
                    const clientId = this.getSessionClientId(sessionData);
                    const client = clientId ? clientsById.get(clientId) : undefined;
                    const participants = this.getSessionParticipants(sessionData, client);
                    const clientIds = Array.from(new Set([
                        ...(Array.isArray(sessionData.clientIds) ? sessionData.clientIds : []),
                        ...participants
                            .filter((participant) => participant.personType === 'client')
                            .map((participant) => participant.personId),
                        clientId,
                    ].filter((candidateId): candidateId is string =>
                        typeof candidateId === 'string' && !!candidateId.trim()
                    ).map((candidateId) => candidateId.trim())));
                    const therapistIds = Array.from(new Set([
                        ...(Array.isArray(sessionData.therapistIds) ? sessionData.therapistIds : []),
                        ...participants
                            .filter((participant) => participant.personType === 'therapist')
                            .map((participant) => participant.personId),
                    ].filter((candidateId): candidateId is string =>
                        typeof candidateId === 'string' && !!candidateId.trim()
                    ).map((candidateId) => candidateId.trim())));
                    const clientName = sessionData.clientName ||
                        sessionData.clientDisplayName ||
                        this.formatClientName(client);
                    const inferredProviderId = this.getSessionProviderId(sessionData, client);

                    return {
                        ...sessionData,
                        id,
                        clientId: clientId || sessionData.clientId,
                        clientName,
                        clientIds,
                        therapistIds,
                        participants,
                        date: sessionData.date || sessionData.startedAt || sessionData.createdAt,
                        healthcareProviderId: sessionData.healthcareProviderId || sessionData.healthcareProvider,
                        providerId: sessionData.providerId,
                        inferredProviderId,
                    } as ParsedTherapySession;
                })
                .filter((session) => {
                    return session.inferredProviderId === access.healthcareProviderId;
                })
                .sort((leftSession, rightSession) => {
                    const dateDifference = this.getTherapySessionDateMs(rightSession) - this.getTherapySessionDateMs(leftSession);
                    if (dateDifference !== 0) {
                        return dateDifference;
                    }

                    return (leftSession.clientName || '').localeCompare(rightSession.clientName || '');
                })
                .map(({ inferredProviderId: _inferredProviderId, ...session }) => session);

            // Keep recent/pending marker edits, but let a later clean refresh become authoritative.
            const sessionsById = new Map(sessions.map((session) => [session.id, session]));
            this.sessionPlacedTagEdits.forEach((state, sessionId) => {
                const startedWith = tagEditsAtFetchStart.get(sessionId);
                if (!this.isSessionPlacedTagEditCurrent(sessionId, state)) {
                    return;
                }
                if (startedWith?.state === state && startedWith.revision === state.revision &&
                    !startedWith.pending && state.pending.length === 0) {
                    this.sessionPlacedTagEdits.delete(sessionId);
                    return;
                }
                const session = sessionsById.get(sessionId) || this.therapySessions.find((item) => item.id === sessionId);
                if (session) {
                    sessionsById.set(sessionId, {
                        ...session,
                        placedTags: applySessionPlacedTagEdits(state.confirmedTags, state.pending),
                    });
                }
            });
            this.therapySessions = Array.from(sessionsById.values());
            this.therapySessionsQueryable.setState({sessions: this.therapySessions});
        } catch (error) {
            console.error('DATA: fetchTherapySessions(): Error fetching therapy sessions:', error);
            // Keep the active player mounted during temporary failures, but never retain denied audio.
            if (requestId === this.therapySessionsFetchRequestId &&
                this.isScopedAccessCurrent(access, () => true) &&
                (isDataAccessDeniedError(error) || !this.canReadAudioScribeAndReports())) {
                this.clearAudioScribeAndReportsState();
            }
        } finally {
            // A superseded request must not clear the newer read's polling guard.
            if (this.therapySessionsFetchesInFlight.get(fetchScopeKey) === requestId) {
                this.therapySessionsFetchesInFlight.delete(fetchScopeKey);
            }
        }
    }

    // Fetch saved generated reports and scope them to the selected healthcare provider.
    private async fetchTherapySessionReports() {
        const access = this.captureScopedAccess(() => this.canReadAudioScribeAndReports());
        if (!access) {
            this.clearAudioScribeAndReportsState();
            return;
        }

        try {
            const clientsById = new Map(
                [...this.allClients, ...this.clients]
                    .filter((client): client is Client & { id: string } => !!client.id)
                    .map((client) => [client.id, client])
            );
            const providerClientIds = this.clients
                .map((client) => client.id)
                .filter((clientId): clientId is string => !!clientId);
            const reportDocuments = await this.fetchProviderLinkedDocuments(
                TABLE_NAME_REPORTS,
                access.healthcareProviderId,
                providerClientIds,
                () => this.canReadAudioScribeAndReports()
            );
            if (!this.isScopedAccessCurrent(access, () => this.canReadAudioScribeAndReports())) {
                return;
            }

            type ParsedTherapySessionReport = TherapySessionReport & { inferredProviderId: string };
            const reports = reportDocuments
                .map(({ id, data: reportData }): ParsedTherapySessionReport => {
                    const clientId = this.getReportClientId(reportData);
                    const client = clientId ? clientsById.get(clientId) : undefined;
                    const clientName = reportData.clientName ||
                        reportData.clientDisplayName ||
                        this.formatClientName(client);
                    const inferredProviderId = this.getReportProviderId(reportData, client);

                    return {
                        ...reportData,
                        id,
                        clientId: clientId || reportData.clientId,
                        clientName,
                        date: reportData.date || reportData.createdAt || reportData.updatedAt,
                        healthcareProviderId: reportData.healthcareProviderId || reportData.healthcareProvider,
                        providerId: reportData.providerId,
                        status: reportData.status || reportData.reportStatus,
                        attachmentStatus: reportData.attachmentStatus,
                        attachmentProgress: typeof reportData.attachmentProgress === 'string' ? reportData.attachmentProgress : undefined,
                        attachmentWarnings: Array.isArray(reportData.attachmentWarnings) ?
                            reportData.attachmentWarnings.filter((warning: unknown) => typeof warning === 'string') : undefined,
                        reportText: reportData.reportText || reportData.text || reportData.summaryText,
                        error: reportData.error || reportData.reportError,
                        inferredProviderId,
                    } as ParsedTherapySessionReport;
                })
                .filter((report) => report.inferredProviderId === access.healthcareProviderId)
                .sort((leftReport, rightReport) =>
                    this.getTherapySessionReportDateMs(rightReport) - this.getTherapySessionReportDateMs(leftReport)
                )
                .map(({ inferredProviderId: _inferredProviderId, ...report }) => report);

            this.therapySessionReports = reports;
            this.therapySessionReportsQueryable.setState({reports: this.therapySessionReports});
            this.syncTherapySessionReportPolling();
        } catch (error) {
            console.error('DATA: fetchTherapySessionReports(): Error fetching therapy session reports:', error);
            if (this.isScopedAccessCurrent(access, () => this.canReadAudioScribeAndReports())) {
                this.therapySessionReports = [];
                this.therapySessionReportsQueryable.setState({reports: []});
            }
        }
    }

    // Fetch non-audio Client and Team timeline documents within the selected provider.
    private async fetchTimelineDocuments() {
        const access = this.captureScopedAccess(() => this.canReadClients());
        if (!access) {
            this.clearTimelineDocumentsState();
            return;
        }

        try {
            const clientsById = new Map(
                [...this.allClients, ...this.clients]
                    .filter((client): client is Client & { id: string } => !!client.id)
                    .map((client) => [client.id, client])
            );
            const providerClientIds = this.clients
                .map((client) => client.id)
                .filter((clientId): clientId is string => !!clientId);
            const timelineDocumentRecords = await this.fetchProviderLinkedDocuments(
                TABLE_NAME_TIMELINE_DOCUMENTS,
                access.healthcareProviderId,
                providerClientIds,
                () => this.canReadClients()
            );
            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return;
            }

            type ParsedTimelineDocument = TimelineDocument & { inferredProviderId: string };
            const documents = timelineDocumentRecords
                .map(({ id, data: documentData }): ParsedTimelineDocument => {
                    const clientId = this.getTimelineDocumentClientId(documentData);
                    const client = clientId ? clientsById.get(clientId) : undefined;
                    const clientName = documentData.clientName ||
                        documentData.clientDisplayName ||
                        this.formatClientName(client);
                    const participants = this.getSessionParticipants(documentData, client);
                    const therapistIds = Array.from(new Set([
                        documentData.therapistId,
                        ...(Array.isArray(documentData.therapistIds) ? documentData.therapistIds : []),
                        ...participants
                            .filter((participant) => participant.personType === 'therapist')
                            .map((participant) => participant.personId),
                    ].filter((candidateId): candidateId is string =>
                        typeof candidateId === 'string' && !!candidateId.trim()
                    ).map((candidateId) => candidateId.trim())));
                    const inferredProviderId = this.getTimelineDocumentProviderId(documentData, client);

                    return {
                        ...documentData,
                        id,
                        clientId: clientId || documentData.clientId,
                        clientName,
                        therapistId: therapistIds[0],
                        therapistIds,
                        participants,
                        date: documentData.date || documentData.createdAt || documentData.updatedAt,
                        healthcareProviderId: documentData.healthcareProviderId || documentData.healthcareProvider,
                        providerId: documentData.providerId,
                        inferredProviderId,
                    } as ParsedTimelineDocument;
                })
                .filter((document) => document.inferredProviderId === access.healthcareProviderId)
                .sort((leftDocument, rightDocument) =>
                    this.getTimelineDocumentDateMs(rightDocument) - this.getTimelineDocumentDateMs(leftDocument)
                )
                .map(({ inferredProviderId: _inferredProviderId, ...document }) => document);

            this.timelineDocuments = documents;
            this.timelineDocumentsQueryable.setState({documents: this.timelineDocuments});
        } catch (error) {
            console.error('DATA: fetchTimelineDocuments(): Error fetching timeline documents:', error);
            if (this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                this.clearTimelineDocumentsState();
            }
        }
    }

    // Resume loaded jobs after reopening the dashboard and stop polling deleted or terminal reports.
    private syncTherapySessionReportPolling() {
        const processingIds = new Set(this.therapySessionReports
            .filter((report) => report.status === 'processing')
            .map((report) => report.id));
        for (const reportId of Array.from(this.therapySessionReportPollingIds)) {
            if (!processingIds.has(reportId)) {
                this.therapySessionReportPollingIds.delete(reportId);
                delete this.therapySessionReportPollingAttemptsById[reportId];
            }
        }
        for (const reportId of Array.from(processingIds)) {
            this.scheduleTherapySessionReportPolling(reportId);
        }
        if (this.therapySessionReportPollingIds.size === 0 && this.therapySessionReportPollingTimerId) {
            clearTimeout(this.therapySessionReportPollingTimerId);
            this.therapySessionReportPollingTimerId = null;
        }
    }

    private scheduleTherapySessionReportPolling(reportId?: string) {
        // Poll current processing reports, including staged file extraction started in another visit.
        if (!this.canReadAudioScribeAndReports()) {
            this.clearAudioScribeAndReportsState();
            return;
        }

        if (reportId) {
            this.therapySessionReportPollingIds.add(reportId);
            this.therapySessionReportPollingAttemptsById[reportId] =
                this.therapySessionReportPollingAttemptsById[reportId] || 0;
        }

        if (this.therapySessionReportPollingIds.size === 0) {
            return;
        }

        if (this.therapySessionReportPollingTimerId) {
            return;
        }

        this.therapySessionReportPollingTimerId = setTimeout(() => {
            this.therapySessionReportPollingTimerId = null;
            void this.pollTherapySessionReports();
        }, THERAPY_SESSION_REPORT_POLL_INTERVAL_MS);
    }

    private async pollTherapySessionReports() {
        // Refresh report documents until each background report leaves the processing state.
        if (this.therapySessionReportPollingIds.size === 0 || !this.canReadAudioScribeAndReports()) {
            if (!this.canReadAudioScribeAndReports()) {
                this.clearAudioScribeAndReportsState();
            }
            return;
        }

        await this.fetchTherapySessionReports();

        Array.from(this.therapySessionReportPollingIds).forEach((reportId) => {
            const report = this.therapySessionReports.find((therapyReport) => therapyReport.id === reportId);
            const nextAttemptCount = (this.therapySessionReportPollingAttemptsById[reportId] || 0) + 1;

            if (report && report.status !== 'processing') {
                this.therapySessionReportPollingIds.delete(reportId);
                delete this.therapySessionReportPollingAttemptsById[reportId];
                return;
            }

            // Loaded jobs can legitimately take longer than the missing-report retry window.
            if (!report && nextAttemptCount >= THERAPY_SESSION_REPORT_MAX_POLL_ATTEMPTS) {
                this.therapySessionReportPollingIds.delete(reportId);
                delete this.therapySessionReportPollingAttemptsById[reportId];
                return;
            }

            this.therapySessionReportPollingAttemptsById[reportId] = nextAttemptCount;
        });

        if (this.therapySessionReportPollingIds.size > 0) {
            this.scheduleTherapySessionReportPolling();
        }
    }

    // Atomically create a missing default without replacing an existing or concurrently created campaign.
    private async ensureDefaultCommsCampaign(access: ScopedAccessSnapshot): Promise<CommsCampaign | null> {
        const campaignRef = doc(db, TABLE_NAME_COMMS_CAMPAIGNS, `${access.healthcareProviderId}_default`);
        return runTransaction(db, async (transaction) => {
            if (!this.isScopedAccessCurrent(access, () => this.canReadMessaging())) {
                return null;
            }
            const campaignDoc = await transaction.get(campaignRef);
            if (!this.isScopedAccessCurrent(access, () => this.canReadMessaging())) {
                return null;
            }

            if (campaignDoc.exists()) {
                const campaignData = campaignDoc.data();
                if (campaignData.healthcareProvider !== access.healthcareProviderId) {
                    throw new Error('The default communications campaign belongs to another healthcare provider.');
                }
                // Normalize legacy fields against the transaction's current data, preserving custom content and ids.
                const normalizedMessages = ensureCampaignMessagesHaveIds(campaignData.messages || []);
                if (normalizedMessages.changed) {
                    transaction.update(campaignRef, {messages: normalizedMessages.messages});
                }
                return {
                    id: campaignDoc.id,
                    healthcareProvider: campaignData.healthcareProvider,
                    messages: normalizedMessages.messages,
                    name: campaignData.name,
                };
            }

            // Use current server metadata so a stale dashboard cannot select the wrong template or name.
            const providerDoc = await transaction.get(doc(db, TABLE_NAME_HEALTHCARE_PROVIDERS, access.healthcareProviderId));
            if (!this.isScopedAccessCurrent(access, () => this.canReadMessaging())) {
                return null;
            }
            if (!providerDoc.exists()) {
                throw new Error('The healthcare provider no longer exists.');
            }
            const defaultCampaign: CommsCampaign = {
                id: campaignRef.id,
                healthcareProvider: access.healthcareProviderId,
                messages: ensureCampaignMessagesHaveIds(createDefaultCommsCampaignMessages(providerDoc.data())).messages,
                name: 'default',
            };
            transaction.set(campaignRef, this.stripUndefinedFields({
                healthcareProvider: defaultCampaign.healthcareProvider,
                messages: defaultCampaign.messages,
                name: defaultCampaign.name,
            }));
            return defaultCampaign;
        });
    }

    // Publish campaigns only while the original provider and messaging access remain current.
    private async fetchCommsCampaigns() {
        const access = this.captureScopedAccess(() => this.canReadMessaging());
        if (!access) {
            this.clearMessagingState();
            return;
        }

        try {
            let campaigns = await this.loadCommsCampaignsForHealthcareProvider(access.healthcareProviderId, true);
            if (!this.isScopedAccessCurrent(access, () => this.canReadMessaging())) {
                return;
            }

            if (campaigns.length === 0) {
                const defaultCampaign = await this.ensureDefaultCommsCampaign(access);
                if (!defaultCampaign || !this.isScopedAccessCurrent(access, () => this.canReadMessaging())) {
                    return;
                }
                campaigns = [defaultCampaign];
            }
            this.commsCampaigns = campaigns;
            this.commsCampaignsQueryable.setState({campaigns: this.commsCampaigns});
        } catch (error) {
            console.error('DATA: fetchCommsCampaigns(): Error fetching communications campaigns:', error);
            if (this.isScopedAccessCurrent(access, () => this.canReadMessaging())) {
                this.clearMessagingState();
            }
        }
    }

    // must have a healthcare provider id set
    private async fetchClients() {
        const access = this.captureScopedAccess(() => this.canReadClients());
        if (!access) {
            this.clearClientsState();
            this.clearTimelineDocumentsState();
            this.clearAudioScribeAndReportsState();
            return;
        }

        const fetchScopeKey = `${access.generation}:${access.healthcareProviderId}`;
        if (this.clientsFetchInFlightScopes.has(fetchScopeKey)) {
            return;
        }

        this.clientsFetchInFlightScopes.add(fetchScopeKey);
        try {
            let allClients = await this.fetchProviderClientsFromFirestore(access.healthcareProviderId);
            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return;
            }

            try {
                const coalescedCareRecipientCount = await this.coalesceDetectedCareRecipientDuplicates(allClients);
                if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                    return;
                }
                if (coalescedCareRecipientCount > 0) {
                    console.log(`DATA: fetchClients(): Coalesced ${coalescedCareRecipientCount} duplicate care recipient record(s)`);
                    allClients = await this.fetchProviderClientsFromFirestore(access.healthcareProviderId);
                }
            } catch (error) {
                console.error('DATA: fetchClients(): Error coalescing duplicate care recipients:', error);
            }

            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return;
            }
            const providerClients = await this.fetchProviderClientsFromFirestore(access.healthcareProviderId);
            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return;
            }
            await this.fetchClientAuthTimes(providerClients, access);
            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return;
            }
            // Keep relationship navigation clients aligned with the provider list's Auth metadata.
            const providerClientById = new Map(providerClients.map((client) => [client.id, client]));
            allClients.forEach((client) => {
                const providerClient = providerClientById.get(client.id);
                if (providerClient?.lastSignInTime) {
                    client.lastSignInTime = providerClient.lastSignInTime;
                }
                if (providerClient?.authCreationTime) {
                    client.authCreationTime = providerClient.authCreationTime;
                }
            });

            this.allClients = allClients;
            this.clients = providerClients;
            // Remove published app content when its owner is deleted, moved, or reclassified as staff.
            if (this.clientContentSubscription && !providerClients.some((client) =>
                client.id === this.clientContentSubscription?.clientId &&
                (client.userType === 'carer' || client.userType === 'care_recipient'))) {
                this.clearClientContentSubscription();
            }
            // A deleted or reclassified person must lose access to their previous clinical briefing.
            if (this.clientBriefingSubscription && !providerClients.some((client) =>
                client.id === this.clientBriefingSubscription?.clientId && client.userType === 'care_recipient')) {
                this.clearClientBriefingSubscription();
            }
            this.clientsQueryable.setState({allClients: this.allClients, clients: this.clients});
            void this.fetchTherapySessions(true);
            void this.fetchTherapySessionReports();
            void this.fetchTimelineDocuments();
            void this.fetchClientActivity(this.clients, access);
        }
        catch (e) {
            console.error('Error fetching clients: ', e);
            // Preserve the last complete client list and its recordings until a refresh succeeds.
            if (this.isScopedAccessCurrent(access, () => true) &&
                (isDataAccessDeniedError(e) || !this.canReadClients())) {
                this.clearClientsState();
                this.clearTimelineDocumentsState();
                this.clearAudioScribeAndReportsState();
            }
        } finally {
            this.clientsFetchInFlightScopes.delete(fetchScopeKey);
        }
    }

    private async fetchClientActivity(clients: Array<Client>, parentAccess?: ScopedAccessSnapshot) {
        const access = parentAccess || this.captureScopedAccess(() => this.canReadClients());
        if (!access || !this.isScopedAccessCurrent(access, () => this.canReadClients())) {
            return;
        }

        const emptyActivity = Object.fromEntries(
            clients
                .filter(client => !!client.id)
                .map(client => [client.id as string, Array(14).fill(0)])
        ) as Record<string, Array<number>>;
        const emptyTimeline = Object.fromEntries(
            clients
                .filter(client => !!client.id)
                .map(client => [client.id as string, [] as Array<ClientActivityPoint>])
        ) as Record<string, Array<ClientActivityPoint>>;
        const emptySessionMinutesTimeline = Object.fromEntries(
            clients
                .filter(client => !!client.id)
                .map(client => [client.id as string, [] as Array<ClientActivityPoint>])
        ) as Record<string, Array<ClientActivityPoint>>;
        const emptyStages = Object.fromEntries(
            clients
                .filter(client => !!client.id)
                .map(client => [client.id as string, 'referral_created_not_signed_in' as CommsCampaignStage])
        ) as Record<string, CommsCampaignStage>;

        if (clients.length === 0) {
            this.clientActivityByClientId = {};
            this.clientActivityTimelineByClientId = {};
            this.clientSessionMinutesTimelineByClientId = {};
            this.clientSignUpTimeByClientId = {};
            this.clientStageByClientId = {};
            this.clientMobileAppUsageByClientId = {};
            this.clientMobileAppUsageStatus = 'ready';
            this.clientActivityQueryable.setState({
                activityByClientId: this.clientActivityByClientId,
                activityTimelineByClientId: this.clientActivityTimelineByClientId,
                sessionMinutesTimelineByClientId: this.clientSessionMinutesTimelineByClientId,
                signUpTimeByClientId: this.clientSignUpTimeByClientId,
                stageByClientId: this.clientStageByClientId,
                mobileAppUsageByClientId: this.clientMobileAppUsageByClientId,
                mobileAppUsageStatus: this.clientMobileAppUsageStatus,
            });
            return;
        }

        const clientIdsByUserId = new Map<string, string>();
        clients.forEach((client) => {
            const clientId = client.id;
            if (clientId) {
                // Query both aliases so legacy activity is not missed after an account is linked.
                getClientActivityIdentifiers(client).forEach((userId) => clientIdsByUserId.set(userId, clientId));
            }
        });

        if (clientIdsByUserId.size === 0) {
            this.clientActivityByClientId = emptyActivity;
            this.clientActivityTimelineByClientId = emptyTimeline;
            this.clientSessionMinutesTimelineByClientId = emptySessionMinutesTimeline;
            this.clientSignUpTimeByClientId = {};
            this.clientStageByClientId = emptyStages;
            this.clientMobileAppUsageByClientId = {};
            this.clientMobileAppUsageStatus = 'ready';
            this.clientActivityQueryable.setState({
                activityByClientId: this.clientActivityByClientId,
                activityTimelineByClientId: this.clientActivityTimelineByClientId,
                sessionMinutesTimelineByClientId: this.clientSessionMinutesTimelineByClientId,
                signUpTimeByClientId: this.clientSignUpTimeByClientId,
                stageByClientId: this.clientStageByClientId,
                mobileAppUsageByClientId: this.clientMobileAppUsageByClientId,
                mobileAppUsageStatus: this.clientMobileAppUsageStatus,
            });
            return;
        }

        try {
            const startDate = new Date();
            startDate.setHours(0, 0, 0, 0);
            startDate.setDate(startDate.getDate() - 13);

            // An unchecked activity response must not be displayed as a confirmed zero families.
            this.clientMobileAppUsageStatus = 'loading';
            this.clientActivityQueryable.setState({
                activityByClientId: this.clientActivityByClientId,
                activityTimelineByClientId: this.clientActivityTimelineByClientId,
                sessionMinutesTimelineByClientId: this.clientSessionMinutesTimelineByClientId,
                signUpTimeByClientId: this.clientSignUpTimeByClientId,
                stageByClientId: this.clientStageByClientId,
                mobileAppUsageByClientId: this.clientMobileAppUsageByClientId,
                mobileAppUsageStatus: this.clientMobileAppUsageStatus,
            });

            // ActivityLog is read through a provider-authorized callable because
            // per-client relationship lookups exceed Firestore query rule limits.
            const activityDocuments = await fetchProviderClientActivityDocuments(
                access.healthcareProviderId,
                Array.from(clientIdsByUserId.keys())
            );
            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return;
            }

            const nextActivityByClientId = {
                ...emptyActivity,
            };
            const completedTopicsTimelineMapsByClientId = Object.fromEntries(
                Object.keys(emptyTimeline).map((clientId) => [clientId, new Map<string, number>()])
            ) as Record<string, Map<string, number>>;
            const sessionDurationMsByClientId = Object.fromEntries(
                Object.keys(emptySessionMinutesTimeline).map((clientId) => [clientId, new Map<string, number>()])
            ) as Record<string, Map<string, number>>;
            const activityEventsByClientId = Object.fromEntries(
                Object.keys(emptyTimeline).map((clientId) => [clientId, [] as Array<{ type: string; timestamp: Date }>])
            ) as Record<string, Array<{ type: string; timestamp: Date }>>;

            activityDocuments.forEach((activityDocument) => {
                const activityData = activityDocument.data;
                const userId = activityData.userId;
                const clientId = clientIdsByUserId.get(userId);
                if (!clientId) {
                    return;
                }

                const timestamp = activityData.timestamp;
                const activityDate = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
                if (isNaN(activityDate.getTime())) {
                    return;
                }

                const activityType = String(activityData.type || '');

                activityEventsByClientId[clientId].push({
                    type: activityType,
                    timestamp: activityDate,
                });

                // Keep the topics chart scoped to topic completion events only.
                if (activityType === 'goal_completed') {
                    const activityDateKey = this.getActivityDateKey(activityDate);
                    const currentCount = completedTopicsTimelineMapsByClientId[clientId].get(activityDateKey) || 0;
                    completedTopicsTimelineMapsByClientId[clientId].set(activityDateKey, currentCount + 1);
                }

                // Sum app session duration by local calendar day for the engagement chart.
                if (activityType === 'app_session') {
                    const sessionWindow = this.getAppSessionWindow(activityData.data);
                    if (sessionWindow) {
                        this.addSessionDurationToTimeline(
                            sessionDurationMsByClientId[clientId],
                            sessionWindow.startedAtMs,
                            sessionWindow.endedAtMs
                        );
                    }
                }

                const dayIndex = Math.floor((activityDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
                if (dayIndex < 0 || dayIndex >= 14) {
                    return;
                }

                nextActivityByClientId[clientId][dayIndex] += 1;
            });

            const nextActivityTimelineByClientId = Object.fromEntries(
                Object.entries(completedTopicsTimelineMapsByClientId).map(([clientId, timelineMap]) => ([
                    clientId,
                    Array.from(timelineMap.entries())
                        .sort(([leftDate], [rightDate]) => leftDate.localeCompare(rightDate))
                        .map(([date, count]) => ({ date, count })),
                ]))
            ) as Record<string, Array<ClientActivityPoint>>;
            const nextSessionMinutesTimelineByClientId = Object.fromEntries(
                Object.entries(sessionDurationMsByClientId).map(([clientId, sessionDurationMap]) => ([
                    clientId,
                    Array.from(sessionDurationMap.entries())
                        .sort(([leftDate], [rightDate]) => leftDate.localeCompare(rightDate))
                        .map(([date, durationMs]) => ({
                            date,
                            count: Math.round(durationMs / (60 * 1000)),
                        }))
                        .filter((point) => point.count > 0),
                ]))
            ) as Record<string, Array<ClientActivityPoint>>;
            const clientById = new Map(
                clients
                    .filter((client): client is Client & { id: string } => !!client.id)
                    .map((client) => [client.id, client])
            );
            const nextStageByClientId = Object.fromEntries(
                Object.entries(activityEventsByClientId).map(([clientId, activityEvents]) => ([
                    clientId,
                    this.getClientStage(clientById.get(clientId), activityEvents),
                ]))
            ) as Record<string, CommsCampaignStage>;
            const nextSignUpTimeByClientId = Object.fromEntries(
                Object.entries(activityEventsByClientId).flatMap(([clientId, activityEvents]) => {
                    // The first sign-in event is the dashboard's existing user-sign-up signal.
                    const signUpDate = this.getActivityEventDate(activityEvents, ['user_signed_in'], 'first');
                    return signUpDate ? [[clientId, signUpDate.toISOString()]] : [];
                })
            ) as Record<string, string>;

            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return;
            }
            this.clientActivityByClientId = nextActivityByClientId;
            this.clientActivityTimelineByClientId = nextActivityTimelineByClientId;
            this.clientSessionMinutesTimelineByClientId = nextSessionMinutesTimelineByClientId;
            this.clientSignUpTimeByClientId = nextSignUpTimeByClientId;
            this.clientStageByClientId = nextStageByClientId;
            this.clientMobileAppUsageByClientId = getMobileAppUsageByClientId(clients, activityDocuments);
            this.clientMobileAppUsageStatus = 'ready';
            this.clientActivityQueryable.setState({
                activityByClientId: this.clientActivityByClientId,
                activityTimelineByClientId: this.clientActivityTimelineByClientId,
                sessionMinutesTimelineByClientId: this.clientSessionMinutesTimelineByClientId,
                signUpTimeByClientId: this.clientSignUpTimeByClientId,
                stageByClientId: this.clientStageByClientId,
                mobileAppUsageByClientId: this.clientMobileAppUsageByClientId,
                mobileAppUsageStatus: this.clientMobileAppUsageStatus,
            });
        } catch (error) {
            console.error('DATA: fetchClientActivity(): Error fetching activity log data:', error);
            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return;
            }
            this.clientActivityByClientId = emptyActivity;
            this.clientActivityTimelineByClientId = emptyTimeline;
            this.clientSessionMinutesTimelineByClientId = emptySessionMinutesTimeline;
            this.clientSignUpTimeByClientId = {};
            this.clientStageByClientId = emptyStages;
            this.clientMobileAppUsageByClientId = {};
            this.clientMobileAppUsageStatus = 'error';
            this.clientActivityQueryable.setState({
                activityByClientId: this.clientActivityByClientId,
                activityTimelineByClientId: this.clientActivityTimelineByClientId,
                sessionMinutesTimelineByClientId: this.clientSessionMinutesTimelineByClientId,
                signUpTimeByClientId: this.clientSignUpTimeByClientId,
                stageByClientId: this.clientStageByClientId,
                mobileAppUsageByClientId: this.clientMobileAppUsageByClientId,
                mobileAppUsageStatus: this.clientMobileAppUsageStatus,
            });
        }
    }

    // Fetch runtime-only Firebase Auth timestamps for provider clients.
    private async fetchClientAuthTimes(clients: Array<Client>, access: ScopedAccessSnapshot) {
        if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
            return;
        }

        try {
            const userIds = clients
                .map(c => c.userId || c.id)
                .filter((uid): uid is string => !!uid);
            if (userIds.length === 0) return;

            const functions = getFunctions();
            const getClientsLastSignIn = httpsCallable<
                { userIds: string[] },
                {
                    lastSignInTimes: Record<string, string | null>;
                    creationTimes?: Record<string, string | null>;
                }
            >(functions, 'getClientsLastSignIn');

            const result = await getClientsLastSignIn({ userIds });
            if (!this.isScopedAccessCurrent(access, () => this.canReadClients())) {
                return;
            }
            const { lastSignInTimes, creationTimes = {} } = result.data;

            for (const client of clients) {
                // Older client documents use Auth metadata for timestamps missing in Firestore.
                const authUserId = client.userId || client.id;
                if (!authUserId) {
                    continue;
                }
                if (lastSignInTimes[authUserId]) {
                    client.lastSignInTime = lastSignInTimes[authUserId]!;
                }
                if (creationTimes[authUserId]) {
                    client.authCreationTime = creationTimes[authUserId]!;
                }
            }
        } catch (error) {
            console.error('DATA: fetchClientAuthTimes(): Error fetching client Auth timestamps:', error);
        }
    }

    private emptyCurrentTherapist(): Therapist {
        return {
            id: '',
            name: '',
            healthcareProviderId: '',
            super: false,
            role: 'healthcare_professional',
            email: '',
        };
    }

    private publishCurrentTherapist(therapist: Therapist, lookupGeneration: number) {
        if (lookupGeneration !== this.therapistLookupGeneration) {
            return;
        }

        this.therapistForCurrentUser = {
            ...therapist,
            role: getDashboardRole(therapist),
        };
        this.currentTherapistQueryable.setState(this.therapistForCurrentUser);

        // Reflect live edits and remove manager-only addresses before publishing a changed role.
        this.therapists = this.therapists.map((teamTherapist) =>
            this.getTherapistForSharedCache(teamTherapist.id === therapist.id ?
                this.therapistForCurrentUser : teamTherapist)
        );
        if (this.canReadTherapists() && this.canViewSelectedProviderTeam() &&
            this.therapistsIncludeTeamRoleFields) {
            this.therapistsInCurrentHealthcareProviderQueryable.setState({therapists: this.therapists});
        }

        void this.fetchHealthcareProviders();
        this.reconcileScopedDataAccess();
    }

    private subscribeToCurrentTherapist(therapistDocumentId: string, lookupGeneration: number) {
        this.currentTherapistUnsubscribe?.();
        this.currentTherapistUnsubscribe = onSnapshot(
            doc(db, 'Therapists', therapistDocumentId),
            (therapistSnapshot) => {
                if (lookupGeneration !== this.therapistLookupGeneration) {
                    return;
                }
                if (!therapistSnapshot.exists()) {
                    this.publishCurrentTherapist(this.emptyCurrentTherapist(), lookupGeneration);
                    return;
                }

                const therapistData = therapistSnapshot.data();
                this.publishCurrentTherapist({
                    id: therapistSnapshot.id,
                    ...therapistData,
                    role: getDashboardRole(therapistData),
                } as Therapist, lookupGeneration);
            },
            (error) => {
                console.error('DATA: subscribeToCurrentTherapist(): Therapist listener failed:', error);
                this.publishCurrentTherapist(this.emptyCurrentTherapist(), lookupGeneration);
            }
        );
    }

    private async loadTherapistForUser(uid: string, email: string, lookupGeneration: number) {
        try {
            const authenticatedUser = auth.currentUser;
            if (!authenticatedUser || authenticatedUser.uid !== uid) {
                this.publishCurrentTherapist(this.emptyCurrentTherapist(), lookupGeneration);
                return;
            }

            // Materialize the server grant before the first direct Therapist read or listener.
            const access = await initializeDashboardAccess(
                authenticatedUser,
                email || null,
                agreementProviderIdFromLocation()
            );
            if (lookupGeneration !== this.therapistLookupGeneration || auth.currentUser?.uid !== uid) {
                return;
            }
            // Keep the selected provider aligned with the server-owned grant before publishing dashboard data.
            this.healthcareProviderId = access.providerId;
            this.subscribeToCurrentTherapist(access.therapistId, lookupGeneration);
        } catch (error) {
            console.error('DATA: loadTherapistForUser(): Dashboard access initialization failed:', error);
            this.publishCurrentTherapist(this.emptyCurrentTherapist(), lookupGeneration);
            if (auth.currentUser?.uid === uid) {
                await signOut(auth).catch((signOutError) =>
                    console.error('DATA: loadTherapistForUser(): Could not close the denied session:', signOutError)
                );
            }
        }
    }

    fetchTherapistForUser(uid: string, email: string) {
        const lookupGeneration = ++this.therapistLookupGeneration;
        this.currentTherapistUnsubscribe?.();
        this.currentTherapistUnsubscribe = null;

        if (!uid) {
            console.log('DATA: fetchTherapistForUser(): No authenticated user; clearing Therapist access');
            this.publishCurrentTherapist(this.emptyCurrentTherapist(), lookupGeneration);
            return;
        }

        console.log(`DATA: fetchTherapistForUser(): Fetching therapist for user with uid: ${uid} and email: ${email}`);
        void this.loadTherapistForUser(uid, email, lookupGeneration);
    }

    queryConditions(): Query<ConditionsState> {
        return this.conditionsQueryable.query();
    }

    queryCurrentTherapist(): Query<Therapist> {
        return this.currentTherapistQueryable.query();
    }

    queryTherapistsInCurrentHealthcareProvider(): Query<TherapistsState> {
        return this.therapistsInCurrentHealthcareProviderQueryable.query();
    }
    
    querySpeechNeeds(): Query<SpeechNeedsState> {
        return this.speechNeedsQueryable.query();
    }

    queryAllSpeechNeeds(): Query<SpeechNeedsState> {
        return this.allSpeechNeedsQueryable.query();
    }

    queryCommsCampaigns(): Query<CommsCampaignsState> {
        return this.commsCampaignsQueryable.query();
    }

    queryClientActivity(): Query<ClientActivityState> {
        return this.clientActivityQueryable.query();
    }

    queryUserResponseKeys(): Query<UserResponseKeysState> {
        return this.userResponseKeysQueryable.query();
    }
    
    queryHealthcareProviders(): Query<HealthcareProvidersState> {
        return this.healthcareProvidersQueryable.query();
    }
    
    queryClients(): Query<ClientsState> {
        return this.clientsQueryable.query();
    }

    queryTherapySessions(): Query<TherapySessionsState> {
        return this.therapySessionsQueryable.query();
    }

    queryTherapySessionReports(): Query<TherapySessionReportsState> {
        return this.therapySessionReportsQueryable.query();
    }

    queryTimelineDocuments(): Query<TimelineDocumentsState> {
        return this.timelineDocumentsQueryable.query();
    }

    async addTherapySession(session: Omit<TherapySession, 'id'>, options?: Pick<TimelineFileUploadOptions, 'signal'>): Promise<string> {
        // Cancellation is allowed until committing the session starts its background processing.
        if (options?.signal?.aborted) throw timelineUploadCancelled();
        // Create a Sessions document and refresh the provider-scoped list.
        this.requireAudioMutation(session.healthcareProviderId || session.providerId);
        const client = await this.requireSelectedClient(session.clientId);
        // Recheck feature access and the verified provider after the client lookup.
        this.requireAudioMutation(client?.providerId || session.healthcareProviderId || session.providerId);
        const participants = this.normalizeTherapySessionParticipants(session.participants);
        // New recordings require a participant; older imports can identify one by Client ID.
        if (participants !== undefined ? participants.length === 0 : !client?.id) {
            throw new Error('A session must have at least one participant.');
        }
        const participantFields = participants === undefined ? {} : {
            participants,
            clientIds: participants
                .filter((participant) => participant.personType === 'client')
                .map((participant) => participant.personId),
            therapistIds: participants
                .filter((participant) => participant.personType === 'therapist')
                .map((participant) => participant.personId),
            clientId: participants.find((participant) => participant.personType === 'client')?.personId,
            clientName: participants.find((participant) => participant.personType === 'client')?.displayName,
        };
        const sessionsRef = collection(db, TABLE_NAME_SESSIONS);
        if (options?.signal?.aborted) throw timelineUploadCancelled();
        const newSessionRef = await addDoc(sessionsRef, this.stripUndefinedFields({
            ...session,
            ...participantFields,
            createdAt: session.createdAt ?? serverTimestamp(),
        }));
        console.log('DATA: addTherapySession(): New session:', newSessionRef.id);
        await this.fetchTherapySessions();
        return newSessionRef.id;
    }

    async updateTherapySessionTitle(sessionId: string, title: string): Promise<void> {
        // Persist the editable session title and refresh the provider session list.
        this.requireAudioMutation();
        if (!sessionId) {
            throw new Error('DATA: updateTherapySessionTitle(): missing session id');
        }
        this.requireSelectedTherapySession(sessionId);

        const sessionRef = doc(db, TABLE_NAME_SESSIONS, sessionId);
        await updateDoc(sessionRef, this.stripUndefinedFields({
            title,
            titleUpdatedAt: serverTimestamp(),
            titleUpdatedByUserId: this.user?.authenticatedUser?.uid,
            titleUpdatedByEmail: this.user?.authenticatedUser?.email || null,
        }));
        await this.fetchTherapySessions();
    }

    async updateTherapySessionDate(sessionId: string, date: Date): Promise<void> {
        // Persist the clinician-selected timeline date without rewriting capture metadata.
        this.requireAudioMutation();
        if (!sessionId) {
            throw new Error('DATA: updateTherapySessionDate(): missing session id');
        }
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            throw new Error('DATA: updateTherapySessionDate(): invalid date');
        }
        this.requireSelectedTherapySession(sessionId);

        const sessionRef = doc(db, TABLE_NAME_SESSIONS, sessionId);
        await updateDoc(sessionRef, this.stripUndefinedFields({
            date,
            dateUpdatedAt: serverTimestamp(),
            dateUpdatedByUserId: this.user?.authenticatedUser?.uid,
            dateUpdatedByEmail: this.user?.authenticatedUser?.email || null,
        }));
        await this.fetchTherapySessions();
    }

    async updateTherapySessionClinicalNotes(sessionId: string, clinicalNotes: string): Promise<void> {
        // Persist therapist-authored session notes and refresh the provider session list.
        this.requireAudioMutation();
        if (!sessionId) {
            throw new Error('DATA: updateTherapySessionClinicalNotes(): missing session id');
        }
        this.requireSelectedTherapySession(sessionId);

        const sessionRef = doc(db, TABLE_NAME_SESSIONS, sessionId);
        await updateDoc(sessionRef, this.stripUndefinedFields({
            clinicalNotes,
            clinicalNotesUpdatedAt: serverTimestamp(),
            clinicalNotesUpdatedByUserId: this.user?.authenticatedUser?.uid,
            clinicalNotesUpdatedByEmail: this.user?.authenticatedUser?.email || null,
        }));
        await this.fetchTherapySessions();
    }

    async updateTherapySessionTranscriptUtterances(
        sessionId: string,
        utterances: Array<TherapySessionTranscriptUtterance>
    ): Promise<void> {
        // Save therapist-corrected speaker assignments for the session transcript.
        this.requireAudioMutation();
        if (!sessionId) {
            throw new Error('DATA: updateTherapySessionTranscriptUtterances(): missing session id');
        }
        this.requireSelectedTherapySession(sessionId);

        const sessionRef = doc(db, TABLE_NAME_SESSIONS, sessionId);
        await updateDoc(sessionRef, this.stripUndefinedFields({
            transcriptUtterances: utterances,
            'metadata.utterances': utterances,
            transcriptAssignmentsUpdatedAt: serverTimestamp(),
            transcriptAssignmentsUpdatedByUserId: this.user?.authenticatedUser?.uid,
            transcriptAssignmentsUpdatedByEmail: this.user?.authenticatedUser?.email || null,
        }));
        await this.fetchTherapySessions();
    }

    async updateTherapySessionPlacedTags(
        sessionId: string,
        placedTags: Array<TherapySessionPlacedTag>,
        previousPlacedTags?: Array<TherapySessionPlacedTag>
    ): Promise<void> {
        // Persist timestamped event tags that therapists place on the session waveform.
        this.requireAudioMutation();
        if (!sessionId) {
            throw new Error('DATA: updateTherapySessionPlacedTags(): missing session id');
        }
        this.requireSelectedTherapySession(sessionId);

        const session = this.therapySessions.find((item) => item.id === sessionId)!;
        let state = this.sessionPlacedTagEdits.get(sessionId);
        if (!state || !this.isSessionPlacedTagEditCurrent(sessionId, state)) {
            state = {
                access: this.captureScopedAccess(() => this.canReadAudioScribeAndReports())!,
                scopeKey: this.getSessionPlacedTagScopeKey(),
                userId: this.user?.authenticatedUser?.uid,
                confirmedTags: [...(session.placedTags || [])],
                pending: [],
                revision: 0,
                saving: false,
            };
            this.sessionPlacedTagEdits.set(sessionId, state);
        }

        // Compare with the caller's displayed snapshot so overlapping clicks merge by marker ID.
        const previousById = new Map((previousPlacedTags ?? session.placedTags ?? []).map((tag) => [tag.id, tag]));
        const nextIds = new Set(placedTags.map((tag) => tag.id));
        const addedTags = placedTags.filter((tag) => {
            const previous = previousById.get(tag.id);
            return !previous || Object.keys(tag).some((key) =>
                tag[key as keyof TherapySessionPlacedTag] !== previous[key as keyof TherapySessionPlacedTag]
            );
        }).map((tag) => ({...tag}));
        const removedIds = new Set(Array.from(previousById.keys()).filter((id) => !nextIds.has(id)));
        const completion = new Promise<void>((resolve, reject) => {
            state!.pending.push({addedTags, removedIds, resolve, reject});
        });
        state.revision += 1;
        this.publishSessionPlacedTags(sessionId, state);
        void this.savePendingSessionPlacedTags(sessionId, state);
        return completion;
    }

    // Ignore completions after the selected provider, signed-in user, or audio access changes.
    private isSessionPlacedTagEditCurrent(sessionId: string, state: SessionPlacedTagEditState): boolean {
        return this.sessionPlacedTagEdits.get(sessionId) === state &&
            state.scopeKey === this.getSessionPlacedTagScopeKey() &&
            state.userId === this.user?.authenticatedUser?.uid &&
            this.isScopedAccessCurrent(state.access, () => this.canReadAudioScribeAndReports()) &&
            this.therapySessions.some((session) => session.id === sessionId);
    }

    // Update every timeline subscriber immediately without replacing other session fields.
    private publishSessionPlacedTags(sessionId: string, state: SessionPlacedTagEditState) {
        if (!this.isSessionPlacedTagEditCurrent(sessionId, state)) {
            return;
        }
        const placedTags = applySessionPlacedTagEdits(state.confirmedTags, state.pending);
        this.therapySessions = this.therapySessions.map((session) =>
            session.id === sessionId ? {...session, placedTags} : session
        );
        this.therapySessionsQueryable?.setState({sessions: this.therapySessions});
    }

    // Distinguish permission/identity changes from harmless profile and provider refreshes.
    private getSessionPlacedTagScopeKey(): string {
        return JSON.stringify([
            this.user?.authenticatedUser?.uid,
            this.healthcareProviderId,
            this.therapistForCurrentUser.id,
            this.getCurrentDashboardRole(),
            this.canReadAudioScribeAndReports(),
        ]);
    }

    // Keep queued edits alive through recency/profile snapshots when their access is unchanged.
    private reconcileSessionPlacedTagEdits() {
        const scopeKey = this.getSessionPlacedTagScopeKey();
        const access = this.captureScopedAccess(() => this.canReadAudioScribeAndReports());
        let removedEdits = false;
        this.sessionPlacedTagEdits.forEach((state, sessionId) => {
            if (access && state.scopeKey === scopeKey) {
                state.access = access;
                return;
            }
            state.pending.forEach((edit) => edit.reject(new Error('Your audio access changed. Refresh the page and try again.')));
            state.pending = [];
            this.therapySessions = this.therapySessions.map((session) =>
                session.id === sessionId ? {...session, placedTags: state.confirmedTags} : session
            );
            this.sessionPlacedTagEdits.delete(sessionId);
            removedEdits = true;
        });
        if (removedEdits) {
            this.therapySessionsQueryable?.setState({sessions: this.therapySessions});
        }
    }

    // Cancel queued writes when the audio cache is cleared for a provider/access reset.
    private clearSessionPlacedTagEdits() {
        this.sessionPlacedTagEdits.forEach((state) => {
            state.pending.forEach((edit) => edit.reject(new Error('Your audio access changed. Refresh the page and try again.')));
            state.pending = [];
        });
        this.sessionPlacedTagEdits.clear();
    }

    // Serialize marker writes, dropping only failed edits before replaying subsequent clicks.
    private async savePendingSessionPlacedTags(sessionId: string, state: SessionPlacedTagEditState) {
        if (state.saving) {
            return;
        }
        state.saving = true;
        while (state.pending.length > 0) {
            const edit = state.pending[0];
            if (!this.isSessionPlacedTagEditCurrent(sessionId, state)) {
                state.pending.forEach((pendingEdit) => pendingEdit.reject(new Error('Your audio access changed. Refresh the page and try again.')));
                state.pending = [];
                break;
            }
            const placedTags = applySessionPlacedTagEdits(state.confirmedTags, [edit]);
            try {
                await updateDoc(doc(db, TABLE_NAME_SESSIONS, sessionId), this.stripUndefinedFields({
                    placedTags,
                    placedTagsUpdatedAt: serverTimestamp(),
                    placedTagsUpdatedByUserId: state.userId,
                    placedTagsUpdatedByEmail: this.user?.authenticatedUser?.email || null,
                }));
                state.confirmedTags = placedTags;
                state.pending = state.pending.filter((pendingEdit) => pendingEdit !== edit);
                state.revision += 1;
                this.publishSessionPlacedTags(sessionId, state);
                edit.resolve();
            } catch (error) {
                state.pending = state.pending.filter((pendingEdit) => pendingEdit !== edit);
                state.revision += 1;
                this.publishSessionPlacedTags(sessionId, state);
                edit.reject(error);
            }
        }
        state.saving = false;
    }

    async updateTherapistSessionTags(
        therapistId: string,
        tags: Array<TherapySessionTagDefinition>
    ): Promise<void> {
        // Store each therapist's personal waveform tag list in most-recently-used order.
        this.requireAudioMutation();
        if (!therapistId) {
            throw new Error('DATA: updateTherapistSessionTags(): missing therapist id');
        }
        if (therapistId !== this.therapistForCurrentUser.id) {
            throw new Error('Session tags can only be changed for the signed-in professional.');
        }

        const sortedTags = [...tags].sort((leftTag, rightTag) =>
            (rightTag.lastUsedAtMs || 0) - (leftTag.lastUsedAtMs || 0)
        );
        const therapistRef = doc(db, 'Therapists', therapistId);
        await updateDoc(therapistRef, {
            sessionTags: sortedTags,
        });

        if (this.therapistForCurrentUser.id === therapistId) {
            this.therapistForCurrentUser = {
                ...this.therapistForCurrentUser,
                sessionTags: sortedTags,
            };
            this.currentTherapistQueryable.setState(this.therapistForCurrentUser);
        }

        // Session tags remain in the signed-in user's record, not the shared Team directory.
    }

    async ensureTherapySessionWaveform(sessionId: string): Promise<void> {
        // Ask Functions to backfill compact waveform peaks for imported/legacy sessions.
        this.requireAudioMutation();
        if (!sessionId) {
            throw new Error('DATA: ensureTherapySessionWaveform(): missing session id');
        }
        this.requireSelectedTherapySession(sessionId);

        const functions = getFunctions();
        const ensureTherapySessionWaveform = httpsCallable<
            { sessionId: string },
            { waveformPeaksCount: number; alreadyExisted: boolean }
        >(functions, 'ensureTherapySessionWaveform', {
            timeout: THERAPY_SESSION_REPORT_CALLABLE_TIMEOUT_MS,
        });

        await ensureTherapySessionWaveform({ sessionId });
        await this.fetchTherapySessions();
    }

    async retryTherapySessionTranscription(sessionId: string): Promise<void> {
        // Ask the authenticated backend to claim and reprocess one failed transcript.
        this.requireAudioMutation();
        if (!sessionId) {
            throw new Error('DATA: retryTherapySessionTranscription(): missing session id');
        }
        this.requireSelectedTherapySession(sessionId);

        const functions = getFunctions();
        const retrySessionAudioMetadata = httpsCallable<
            {sessionId: string},
            {status: 'completed'}
        >(functions, 'retrySessionAudioMetadata', {
            timeout: THERAPY_SESSION_REPORT_CALLABLE_TIMEOUT_MS,
        });

        try {
            await retrySessionAudioMetadata({sessionId});
        } finally {
            await this.fetchTherapySessions();
        }
    }

    async generateTherapySessionReport(request: TherapySessionReportGenerationRequest): Promise<string> {
        // Delegate OpenAI calls to Functions so API credentials never reach the browser.
        this.requireAudioMutation(request.healthcareProviderId);
        const client = await this.requireSelectedClient(request.clientId);
        this.requireAudioMutation(client?.providerId || request.healthcareProviderId);
        request.sessionIds.forEach((sessionId) => this.requireSelectedTherapySession(sessionId));
        const functions = getFunctions();
        const generateTherapySessionReport = httpsCallable<
            TherapySessionReportGenerationRequest,
            { reportId: string }
        >(functions, 'generateTherapySessionReport', {
            timeout: THERAPY_SESSION_REPORT_CALLABLE_TIMEOUT_MS,
        });

        try {
            const result = await generateTherapySessionReport(request);
            this.scheduleTherapySessionReportPolling(result.data.reportId);
            return result.data.reportId;
        } finally {
            await this.fetchTherapySessionReports();
        }
    }

    // Fetch extracted evidence only while the user's original provider access remains current.
    async getReportAttachmentContent(clientId: string, documentId: string): Promise<ReportAttachmentContent> {
        const access = this.captureScopedAccess(() => this.canReadAudioScribeAndReports());
        if (!access || !clientId || !documentId) {
            throw new Error('File content is not available without current Client report access.');
        }
        const client = await this.requireSelectedClient(clientId);
        this.requireAudioMutation(client?.providerId);
        this.requireSelectedTimelineDocument({id: documentId});
        if (!this.isScopedAccessCurrent(access, () => this.canReadAudioScribeAndReports())) {
            throw new Error('Your Client access changed. Please reopen the record.');
        }
        const request = httpsCallable<{ clientId: string; documentId: string }, ReportAttachmentContent>(
            getFunctions(), 'getReportAttachmentContent'
        );
        const result = await request({clientId, documentId});
        if (!this.isScopedAccessCurrent(access, () => this.canReadAudioScribeAndReports())) {
            throw new Error('Your Client access changed. Please reopen the record.');
        }
        return result.data;
    }

    // Explicit corrections are version checked on the server before use in future reports.
    async saveReportAttachmentContent(clientId: string, documentId: string, sourceVersion: string, text: string): Promise<void> {
        const access = this.captureScopedAccess(() => this.canReadAudioScribeAndReports());
        if (!access || !clientId || !documentId || !sourceVersion || !text.trim()) {
            throw new Error('File corrections require current Client report access and nonempty content.');
        }
        const client = await this.requireSelectedClient(clientId);
        this.requireAudioMutation(client?.providerId);
        this.requireSelectedTimelineDocument({id: documentId});
        if (!this.isScopedAccessCurrent(access, () => this.canReadAudioScribeAndReports())) {
            throw new Error('Your Client access changed. Please reopen the record.');
        }
        const request = httpsCallable<
            { clientId: string; documentId: string; sourceVersion: string; text: string },
            { saved: true }
        >(getFunctions(), 'saveReportAttachmentContent');
        await request({clientId, documentId, sourceVersion, text});
        if (!this.isScopedAccessCurrent(access, () => this.canReadAudioScribeAndReports())) {
            throw new Error('Your Client access changed. Please reopen the record.');
        }
    }

    async updateTherapySessionReportText(reportId: string, reportText: string): Promise<void> {
        // Persist therapist edits to a generated report and refresh the Reports cache.
        this.requireAudioMutation();
        if (!reportId) {
            throw new Error('DATA: updateTherapySessionReportText(): missing report id');
        }
        this.requireSelectedTherapySessionReport(reportId);

        await updateDoc(doc(db, TABLE_NAME_REPORTS, reportId), this.stripUndefinedFields({
            reportText,
            reportTextUpdatedAt: serverTimestamp(),
            reportTextUpdatedByUserId: this.user?.authenticatedUser?.uid,
            reportTextUpdatedByEmail: this.user?.authenticatedUser?.email || null,
            updatedAt: serverTimestamp(),
            updatedByUserId: this.user?.authenticatedUser?.uid,
            updatedByEmail: this.user?.authenticatedUser?.email || null,
        }));
        await this.fetchTherapySessionReports();
    }

    async deleteTherapySessionReport(report: TherapySessionReport): Promise<void> {
        // Remove the generated report document and refresh the local Reports cache.
        this.requireAudioMutation(report.healthcareProviderId || report.providerId);
        if (!report.id) {
            throw new Error('DATA: deleteTherapySessionReport(): missing report id');
        }
        this.requireSelectedTherapySessionReport(report.id);

        await deleteDoc(doc(db, TABLE_NAME_REPORTS, report.id));
        await this.fetchTherapySessionReports();
    }

    async deleteTherapySession(session: TherapySession): Promise<void> {
        // Delete the session media objects first, then remove the Sessions document.
        this.requireAudioMutation(session.healthcareProviderId || session.providerId);
        if (!session.id) {
            throw new Error('DATA: deleteTherapySession(): missing session id');
        }
        this.requireSelectedTherapySession(session.id);

        const sessionStoragePaths = Array.from(new Set([
            session.storagePath,
            session.originalMediaStoragePath,
            session.importMediaStoragePath,
        ].filter((path): path is string => !!path)));
        for (const sessionStoragePath of sessionStoragePaths) {
            try {
                await deleteObject(storageRef(storage, sessionStoragePath));
            } catch (error) {
                if ((error as { code?: string })?.code !== 'storage/object-not-found') {
                    throw error;
                }
            }
        }

        await deleteDoc(doc(db, TABLE_NAME_SESSIONS, session.id));
        await this.fetchTherapySessions();
    }

    async addTimelineClinicalNote(documentData: Omit<TimelineDocument, 'id' | 'kind'>): Promise<string> {
        // Create a standalone clinical note for a Client or Team member timeline.
        const providerId = documentData.healthcareProviderId || documentData.providerId || this.healthcareProviderId;
        this.requireClientsMutation(providerId);
        const ownership = await this.normalizeTimelineDocumentOwnership(documentData, providerId);
        this.requireClientsMutation(providerId);
        const timelineDocumentsRef = collection(db, TABLE_NAME_TIMELINE_DOCUMENTS);
        const newDocumentRef = await addDoc(timelineDocumentsRef, this.stripUndefinedFields({
            ...documentData,
            ...ownership,
            kind: 'clinical_note',
            healthcareProviderId: providerId,
            providerId,
            clinicalNotes: documentData.clinicalNotes || '',
            date: documentData.date ?? serverTimestamp(),
            createdAt: documentData.createdAt ?? serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdByUserId: this.user?.authenticatedUser?.uid,
            createdByEmail: this.user?.authenticatedUser?.email || null,
            updatedByUserId: this.user?.authenticatedUser?.uid,
            updatedByEmail: this.user?.authenticatedUser?.email || null,
        }));
        await this.fetchTimelineDocuments();
        return newDocumentRef.id;
    }

    async updateTimelineClinicalNote(documentId: string, clinicalNotes: string): Promise<void> {
        // Persist edits for a standalone clinical note document.
        this.requireClientsMutation();
        if (!documentId) {
            throw new Error('DATA: updateTimelineClinicalNote(): missing document id');
        }
        this.requireSelectedTimelineDocument({ id: documentId });

        await updateDoc(doc(db, TABLE_NAME_TIMELINE_DOCUMENTS, documentId), this.stripUndefinedFields({
            clinicalNotes,
            clinicalNotesUpdatedAt: serverTimestamp(),
            clinicalNotesUpdatedByUserId: this.user?.authenticatedUser?.uid,
            clinicalNotesUpdatedByEmail: this.user?.authenticatedUser?.email || null,
            updatedAt: serverTimestamp(),
            updatedByUserId: this.user?.authenticatedUser?.uid,
            updatedByEmail: this.user?.authenticatedUser?.email || null,
        }));
        await this.fetchTimelineDocuments();
    }

    async updateTimelineDocumentTitle(documentId: string, title: string): Promise<void> {
        // Rename either standalone notes or uploaded files while preserving original filenames.
        this.requireClientsMutation();
        if (!documentId) {
            throw new Error('DATA: updateTimelineDocumentTitle(): missing document id');
        }
        const nextTitle = title.trim();
        if (!nextTitle) {
            throw new Error('DATA: updateTimelineDocumentTitle(): missing title');
        }
        this.requireSelectedTimelineDocument({ id: documentId });

        await updateDoc(doc(db, TABLE_NAME_TIMELINE_DOCUMENTS, documentId), this.stripUndefinedFields({
            title: nextTitle,
            titleUpdatedAt: serverTimestamp(),
            titleUpdatedByUserId: this.user?.authenticatedUser?.uid,
            titleUpdatedByEmail: this.user?.authenticatedUser?.email || null,
            updatedAt: serverTimestamp(),
            updatedByUserId: this.user?.authenticatedUser?.uid,
            updatedByEmail: this.user?.authenticatedUser?.email || null,
        }));
        await this.fetchTimelineDocuments();
    }

    async updateTimelineDocumentDate(documentId: string, date: Date): Promise<void> {
        // Change the timeline date while retaining immutable creation and upload timestamps.
        this.requireClientsMutation();
        if (!documentId) {
            throw new Error('DATA: updateTimelineDocumentDate(): missing document id');
        }
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            throw new Error('DATA: updateTimelineDocumentDate(): invalid date');
        }
        this.requireSelectedTimelineDocument({ id: documentId });

        await updateDoc(doc(db, TABLE_NAME_TIMELINE_DOCUMENTS, documentId), this.stripUndefinedFields({
            date,
            dateUpdatedAt: serverTimestamp(),
            dateUpdatedByUserId: this.user?.authenticatedUser?.uid,
            dateUpdatedByEmail: this.user?.authenticatedUser?.email || null,
            updatedAt: serverTimestamp(),
            updatedByUserId: this.user?.authenticatedUser?.uid,
            updatedByEmail: this.user?.authenticatedUser?.email || null,
        }));
        await this.fetchTimelineDocuments();
    }

    async addTimelineFileDocument(
        documentData: Omit<TimelineDocument, 'id' | 'kind' | 'storagePath' | 'originalName' | 'contentType' | 'sizeBytes'>,
        file: File,
        options: TimelineFileUploadOptions = {}
    ): Promise<string> {
        // Upload the file first, then create the timeline metadata document that points to it.
        const checkCancellation = () => {
            if (options.signal?.aborted) throw timelineUploadCancelled();
        };
        checkCancellation();
        const validationError = file ? getTimelineFileError(file) : 'Choose a file to upload.';
        if (validationError) throw new Error(validationError);

        const providerId = documentData.healthcareProviderId || documentData.providerId || this.healthcareProviderId;
        this.requireClientsMutation(providerId);
        const ownership = await this.normalizeTimelineDocumentOwnership(documentData, providerId);
        this.requireClientsMutation(providerId);
        if (!providerId || (!ownership.clientId && !ownership.therapistId)) {
            throw new Error('DATA: addTimelineFileDocument(): missing provider or timeline owner id');
        }

        checkCancellation();
        const timelineDocumentRef = doc(collection(db, TABLE_NAME_TIMELINE_DOCUMENTS));
        const safeFileName = this.sanitizeStorageFileName(file.name);
        // Keep Team files distinct from Client paths even if their person IDs happen to match.
        const ownerPath = ownership.clientId || `TeamMembers/${ownership.therapistId}`;
        const storagePath = `HealthcareProviders/${providerId}/TimelineDocuments/${ownerPath}/${timelineDocumentRef.id}-${safeFileName}`;

        let metadataWritten = false;
        let fileUploaded = false;
        try {
            // Expose real transfer cancellation and progress without changing file ownership.
            const uploadTask = uploadBytesResumable(storageRef(storage, storagePath), file, {
                contentType: file.type || 'application/octet-stream',
            });
            const cancelTransfer = () => { uploadTask.cancel(); };
            options.signal?.addEventListener('abort', cancelTransfer);
            const unsubscribe = uploadTask.on('state_changed', (snapshot) => {
                options.onProgress?.(Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100));
            });
            try {
                if (options.signal?.aborted) cancelTransfer();
                await uploadTask;
                fileUploaded = true;
            } finally {
                unsubscribe();
                options.signal?.removeEventListener('abort', cancelTransfer);
            }
            checkCancellation();
            options.onProgress?.(100);
            checkCancellation();
            this.requireClientsMutation(providerId);
            await setDoc(timelineDocumentRef, this.stripUndefinedFields({
                ...documentData,
                ...ownership,
                kind: 'file',
                title: documentData.title || file.name,
                healthcareProviderId: providerId,
                providerId,
                originalName: file.name,
                storagePath,
                contentType: file.type || 'application/octet-stream',
                sizeBytes: file.size,
                date: documentData.date ?? serverTimestamp(),
                createdAt: documentData.createdAt ?? serverTimestamp(),
                updatedAt: serverTimestamp(),
                uploadedAt: serverTimestamp(),
                uploadedByUserId: this.user?.authenticatedUser?.uid,
                uploadedByEmail: this.user?.authenticatedUser?.email || null,
                createdByUserId: this.user?.authenticatedUser?.uid,
                createdByEmail: this.user?.authenticatedUser?.email || null,
                updatedByUserId: this.user?.authenticatedUser?.uid,
                updatedByEmail: this.user?.authenticatedUser?.email || null,
            }));
            metadataWritten = true;
            checkCancellation();
        } catch (error) {
            // Roll back a cancellation arriving during the metadata write before removing its file.
            try {
                if (metadataWritten) await deleteDoc(timelineDocumentRef);
                // Cancelled resumable transfers have no stored object; only completed files need deletion.
                if (fileUploaded) await deleteObject(storageRef(storage, storagePath)).catch((deleteError) => {
                    if ((deleteError as { code?: string })?.code !== 'storage/object-not-found') throw deleteError;
                });
            } catch (cleanupError) {
                console.error('DATA: addTimelineFileDocument(): Could not clean up upload:', cleanupError);
                throw new Error('Could not clean up the unfinished upload. Refresh the timeline before trying again.');
            }
            if (options.signal?.aborted) throw timelineUploadCancelled();
            throw error;
        }

        // Once saved, a late close request must report success rather than remove a completed entry.
        await this.fetchTimelineDocuments();
        return timelineDocumentRef.id;
    }

    async getTimelineFileDownloadUrl(documentData: TimelineDocument): Promise<string> {
        // Resolve a provider-authorized Storage download URL for preview or download.
        this.requireSelectedTimelineDocument(documentData);
        if (!documentData.storagePath) {
            throw new Error('This timeline document does not have a stored file.');
        }

        return await getDownloadURL(storageRef(storage, documentData.storagePath));
    }

    async downloadTimelineFileDocument(documentData: TimelineDocument): Promise<void> {
        // Use a temporary anchor so browsers download when possible and open otherwise.
        const downloadUrl = await this.getTimelineFileDownloadUrl(documentData);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = documentData.originalName || documentData.title || 'timeline-document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    async deleteTimelineDocument(documentData: TimelineDocument): Promise<void> {
        // Remove any linked Storage object before deleting the timeline metadata document.
        this.requireSelectedTimelineDocument(documentData);
        if (!documentData.id) {
            throw new Error('DATA: deleteTimelineDocument(): missing document id');
        }

        if (documentData.storagePath) {
            try {
                await deleteObject(storageRef(storage, documentData.storagePath));
            } catch (error) {
                if ((error as { code?: string })?.code !== 'storage/object-not-found') {
                    throw error;
                }
            }
        }

        await deleteDoc(doc(db, TABLE_NAME_TIMELINE_DOCUMENTS, documentData.id));
        await this.fetchTimelineDocuments();
    }

    async addClient(client: Client): Promise<string> {
        this.requireClientsMutation(client.providerId);
        const clientWithSpeechNeeds = withClientSpeechNeeds(client);
        if (isCareRecipientClient(clientWithSpeechNeeds)) {
            const existingCareRecipients = await this.findExistingCareRecipientsForClient(clientWithSpeechNeeds);
            if (existingCareRecipients.length > 0) {
                const existingClientId = await this.coalesceCareRecipientGroup(existingCareRecipients, clientWithSpeechNeeds);
                console.log('DATA: addClient(): Reused existing care recipient:', existingClientId);
                return existingClientId;
            }
        }

        // add a new client object with the Firebase-generated ID
        const clientsRef = collection(db, TABLE_NAME_CLIENTS);
        const { id: _omittedClientId, ...clientWithoutId } = clientWithSpeechNeeds;
        const newClientPayload = this.stripUndefinedFields({
            ...clientWithoutId,
            accessMethod: 'referral',
            createdAt: clientWithoutId.createdAt ?? serverTimestamp(),
        }) as Record<string, any>;
        delete newClientPayload.friendlyId;
        const newClientRef = await addDoc(clientsRef, newClientPayload);
        console.log('DATA: addClient(): New client:', newClientRef.id);
        return newClientRef.id;
    }

    async updateClient(client: Client): Promise<void> {
        this.requireClientsMutation(client.providerId);
        const previousClient = await this.requireSelectedClient(client.id);
        this.requireClientsMutation(previousClient?.providerId);
        if (!client.id) {
            console.error('DATA: updateClient(): Client has no id');
            return;
        }
        console.log('DATA: updateClient(): Updating client:', client.id);
        const clientRef = doc(db, TABLE_NAME_CLIENTS, client.id);
        // The Firestore document id is the source of truth, so remove any persisted `id` field.
        const { id: _omittedClientId, ...clientWithoutId } = withClientSpeechNeeds(client);
        const updatePayload = this.stripUndefinedFields(clientWithoutId) as Record<string, any>;
        delete updatePayload.friendlyId;
        if (previousClient &&
            isCareRecipientClient(client) &&
            !speechNeedSetsMatch(getClientSpeechNeeds(previousClient), getClientSpeechNeeds(clientWithoutId))) {
            updatePayload.speechNeedsChangedAt = serverTimestamp();
        }
        updatePayload.id = deleteField();
        updatePayload.commsCampaignId = client.commsCampaignId ? client.commsCampaignId : deleteField();
        await updateDoc(clientRef, updatePayload);
    }

    async deleteClient(client: Client): Promise<void> {
        this.requireClientsMutation(client.providerId);
        const selectedClient = await this.requireSelectedClient(client.id);
        this.requireClientsMutation(selectedClient?.providerId);
        if (!client.id) return;

        // Server cleanup keeps legacy Family relationships provider-scoped and atomic before Client deletion.
        const deleteProviderClient = httpsCallable<
            { healthcareProviderId: string; clientId: string },
            { healthcareProviderId: string; clientId: string; deleted: true }
        >(getFunctions(), 'deleteProviderClient');
        const result = await deleteProviderClient({
            healthcareProviderId: this.healthcareProviderId,
            clientId: client.id,
        });
        if (!result.data.deleted || result.data.healthcareProviderId !== this.healthcareProviderId ||
            result.data.clientId !== client.id) {
            throw new Error('Client deletion completed with an unexpected provider scope.');
        }
    }

    async updateTherapistPasswordChanged(therapistId: string): Promise<void> {
        if (!therapistId) {
            console.error('DATA: updateTherapistPasswordChanged(): No therapist ID provided');
            return;
        }
        
        try {
            console.log('DATA: updateTherapistPasswordChanged(): Updating therapist:', therapistId);
            const therapistRef = doc(db, 'Therapists', therapistId);
            await updateDoc(therapistRef, {
                mustChangePassword: false
            });
            
            // Update local state
            this.therapistForCurrentUser = {
                ...this.therapistForCurrentUser,
                mustChangePassword: false
            };
            this.currentTherapistQueryable.setState(this.therapistForCurrentUser);
            
            console.log('DATA: updateTherapistPasswordChanged(): Successfully updated therapist');
        } catch (error) {
            console.error('DATA: updateTherapistPasswordChanged(): Error updating therapist:', error);
            throw error;
        }
    }

    async updateTherapistProfile(
        therapistId: string,
        updates: { name: string; role: DashboardRole }
    ): Promise<void> {
        // Protect stale callbacks locally; the callable repeats every authorization check.
        const name = updates.name.trim();
        const actorRole = this.getCurrentDashboardRole();
        const targetTherapist = this.therapists.find((therapist) => therapist.id === therapistId);
        const access = this.captureScopedAccess(() => this.hasSelectedProviderFeature('organisation_team_manage'));
        if (!therapistId || !name || !access || !targetTherapist ||
            !canAssignDashboardRole(actorRole, getDashboardRole(targetTherapist)) ||
            !canAssignDashboardRole(actorRole, updates.role)) {
            throw new Error('Your role cannot edit this dashboard user.');
        }

        const functions = getFunctions();
        const updateDashboardUser = httpsCallable(functions, 'updateDashboardUser');
        await updateDashboardUser({
            healthcareProviderId: access.healthcareProviderId,
            therapistId,
            name,
            role: updates.role,
        });
        if (!this.isScopedAccessCurrent(
            access,
            () => this.hasSelectedProviderFeature('organisation_team_manage')
        )) {
            return;
        }

        const superUser = isRootOrSuperRole(updates.role);
        this.therapists = this.therapists.map((therapist) =>
            therapist.id === therapistId ? this.getTherapistForSharedCache({
                ...therapist,
                name,
                role: updates.role,
                super: superUser,
            }) : therapist
        );
        this.therapistsInCurrentHealthcareProviderQueryable.setState({therapists: this.therapists});

        if (this.therapistForCurrentUser.id === therapistId) {
            this.therapistForCurrentUser = {
                ...this.therapistForCurrentUser,
                name,
                role: updates.role,
                super: superUser,
            };
            this.currentTherapistQueryable.setState(this.therapistForCurrentUser);
        }
    }

    async deleteDashboardUser(therapistId: string): Promise<void> {
        // Remove a Team member only while the selected provider and management entitlement remain current.
        const actorRole = this.getCurrentDashboardRole();
        const targetTherapist = this.therapists.find((therapist) => therapist.id === therapistId);
        const access = this.captureScopedAccess(() => this.hasSelectedProviderFeature('organisation_team_manage'));
        if (!access || !targetTherapist ||
            !canAssignDashboardRole(actorRole, getDashboardRole(targetTherapist))) {
            throw new Error('Your role cannot delete this dashboard user.');
        }

        const functions = getFunctions();
        const deleteUser = httpsCallable(functions, 'deleteDashboardUser');
        await deleteUser({
            healthcareProviderId: access.healthcareProviderId,
            therapistId,
        });
        if (!this.isScopedAccessCurrent(
            access,
            () => this.hasSelectedProviderFeature('organisation_team_manage')
        )) {
            return;
        }

        this.therapists = this.therapists.filter((therapist) => therapist.id !== therapistId);
        this.therapistsInCurrentHealthcareProviderQueryable.setState({therapists: this.therapists});
    }

    async createDashboardUser(request: CreateDashboardUserRequest): Promise<Therapist> {
        // Keep stale Team callbacks from creating users after access is revoked.
        const actorRole = this.getCurrentDashboardRole();
        const access = this.captureScopedAccess(() => this.hasSelectedProviderFeature('organisation_team_manage'));
        if (!access ||
            !canAssignDashboardRole(actorRole, request.role)) {
            throw new Error('Your role cannot create this dashboard user.');
        }

        const functions = getFunctions();
        const createUser = httpsCallable<
            CreateDashboardUserRequest & { healthcareProviderId: string },
            Therapist
        >(functions, 'createDashboardUser');
        const result = await createUser({
            ...request,
            healthcareProviderId: access.healthcareProviderId,
        });
        const therapist = result.data;

        if (this.isScopedAccessCurrent(
            access,
            () => this.hasSelectedProviderFeature('organisation_team_manage')
        )) {
            if (this.therapistsIncludeTeamRoleFields) {
                this.therapists = [
                    ...this.therapists.filter((existingTherapist) => existingTherapist.id !== therapist.id),
                    this.getTherapistForSharedCache(therapist),
                ];
                this.therapistsInCurrentHealthcareProviderQueryable.setState({therapists: this.therapists});
            } else {
                void this.fetchTherapistsInCurrentHealthcareProvider();
            }
        }
        return therapist;
    }

    async resendDashboardUserVerificationEmail(therapistId: string): Promise<DashboardUserVerificationResult> {
        // Keep recovery inside the current provider and Team-management grant.
        const targetTherapist = this.therapists.find((therapist) => therapist.id === therapistId);
        const access = this.captureScopedAccess(() => this.hasSelectedProviderFeature('organisation_team_manage'));
        if (!access || !targetTherapist || targetTherapist.signInMethod !== 'email_password') {
            throw new Error('This dashboard user cannot receive an email verification link.');
        }

        const resendVerification = httpsCallable<
            { healthcareProviderId: string; therapistId: string },
            DashboardUserVerificationResult
        >(getFunctions(), 'resendDashboardUserVerificationEmail');
        const result = await resendVerification({
            healthcareProviderId: access.healthcareProviderId,
            therapistId,
        });
        if (result.data.therapistId !== therapistId ||
            typeof result.data.alreadyVerified !== 'boolean' ||
            typeof result.data.verificationEmailSent !== 'boolean' ||
            (!result.data.alreadyVerified && !result.data.verificationEmailSent)) {
            throw new Error('Email verification completed with an unexpected dashboard user.');
        }
        if (!this.isScopedAccessCurrent(
            access,
            () => this.hasSelectedProviderFeature('organisation_team_manage')
        )) {
            return result.data;
        }

        // Reflect verification or a successful resend without a full page reload.
        this.therapists = this.therapists.map((therapist) => therapist.id === therapistId ? {
            ...therapist,
            emailVerified: result.data.alreadyVerified ? true : therapist.emailVerified,
            verificationEmailSentAt: result.data.verificationEmailSent ?
                new Date().toISOString() : therapist.verificationEmailSentAt,
        } : therapist);
        this.therapistsInCurrentHealthcareProviderQueryable.setState({therapists: this.therapists});
        return result.data;
    }

    async addFamily(family: Family): Promise<string> {
        this.requireClientsMutation();
        await this.requireSelectedFamilyClients(family);
        if (family.careRecipient) {
            const existingFamilies = await getDocs(
                query(collection(db, TABLE_NAME_FAMILIES), where('careRecipient', '==', family.careRecipient))
            );
            if (!existingFamilies.empty) {
                const [firstFamilyDoc, ...duplicateFamilyDocs] = existingFamilies.docs;
                const existingFamily = firstFamilyDoc.data() as Family;
                const members = Array.from(new Map([
                    ...(Array.isArray(existingFamily.members) ? existingFamily.members : []),
                    ...(Array.isArray(family.members) ? family.members : []),
                ]
                    .filter((member) => typeof member.userId === 'string' && member.userId.trim() !== '')
                    .map((member) => [member.userId, member])
                ).values());

                await updateDoc(doc(db, TABLE_NAME_FAMILIES, firstFamilyDoc.id), {
                    ...existingFamily,
                    careRecipient: family.careRecipient,
                    members,
                });

                for (const duplicateFamilyDoc of duplicateFamilyDocs) {
                    await deleteDoc(doc(db, TABLE_NAME_FAMILIES, duplicateFamilyDoc.id));
                }

                console.log('DATA: addFamily(): Reused existing family:', firstFamilyDoc.id);
                return firstFamilyDoc.id;
            }
        }

        const generateFamilyId = (): string => {
            const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let result = 'F'; // Start with 'F' prefix
            const charactersLength = characters.length;
            for (let i = 0; i < Constants.INVITE_CODE_LENGTH; i++) {
                result += characters.charAt(Math.floor(Math.random() * charactersLength));
            }
            return result;
        };

        const checkFamilyIdExists = async (familyId: string): Promise<boolean> => {
            const familyDocRef = doc(db, TABLE_NAME_FAMILIES, familyId);
            const familyDoc = await getDoc(familyDocRef);
            return familyDoc.exists();
        };

        let familyId = generateFamilyId();
        while (await checkFamilyIdExists(familyId)) {
            familyId = generateFamilyId();
        }

        const familyDocRef = doc(db, TABLE_NAME_FAMILIES, familyId);
        await setDoc(familyDocRef, {
            ...family,
            createdAt: new Date(),
        });
        console.log('DATA: addFamily(): New family:', familyId);
        return familyId;
    }
}

export function newData(options: {app: App}): Data {
    return new DataImpl(options);
}
